/**
 * The help text of the workbench, as typed data.
 *
 * One topic per section of the Help page (`/help#<id>`). The small help buttons on the
 * queue, the case and the dashboard open the matching topic. The case and queue topics
 * describe the minimal redesign of 12 Oct 2026: three steps, one word per row, one action. Every topic lists in
 * `labels` the exact screen texts it quotes; `tests/help.test.mjs` checks that each of
 * them still exists somewhere in `src/`, so the help cannot drift away from the screen.
 *
 * Writing rules (round 3, 12 Oct 2026): Simplified Technical English. One instruction
 * per sentence, imperative, present tense, active voice with I2Q, you, the customer or
 * F&O as the doer; at most 20 words in an instruction and 25 in a description; the
 * glossary of docs/ux-minimal-redesign.md (inquiry, mail, case, line, item, draft,
 * reply, quotation; never "the tool", "answer", "choose", "part number"); no em dash,
 * never "rep" (say sales people). People are named by role only. The product is
 * "Norican I2Q" (Inquiry to Quote) and has one purpose: customer inquiries to
 * quotations. Screen labels stay verbatim inside double quotes.
 */

import type { IconComponent, IconTone } from "@norican/icons";

import type { FigureName } from "@/components/figures";
import {
  BASIS_ICON,
  CUSTOMER_ICON,
  LEAD_ICON,
  LEGEND,
  PRICE_ICON,
  QTY_DEFAULT_ICON,
  QTY_ISSUE_ICON,
  QUEUE_ICON,
  SOURCE_ICON,
  STATUS_ICON,
  STATUS_TONE,
} from "@/lib/line-icons";

export type HelpTopicId =
  | "training"
  | "what-it-is"
  | "mail-to-quotation"
  | "customer"
  | "case-page"
  | "case-does-not-go-through"
  | "work-faster"
  | "back-for-you"
  | "prepared-replies"
  | "i2q-decided-lines"
  | "deeper-search"
  | "translation"
  | "photos"
  | "queue"
  | "statuses"
  | "learned"
  | "dashboard"
  | "not-yet"
  | "contact"
  | "usage-log"
  | "feedback"
  | "rules"
  | "rules-and-countries"
  | "cost-and-runs"
  | "master-data"
  | "what-runs-today"
  | "business-decisions"
  | "process-mining"
  | "menu";

/** One numbered step of a flow. */
export interface HelpStep {
  title: string;
  text: string;
}

/** A screen text and what it means. `system` is the name in the data, for IT only. */
export interface HelpTerm {
  term: string;
  text: string;
  system?: string;
}

/** One line of "What the icons mean": the icons as drawn on the screen, then the sentence. */
export interface HelpLegendItem {
  icons: { icon: IconComponent; tone?: IconTone }[];
  text: string;
}

export type HelpBlock =
  | { kind: "text"; text: string }
  | { kind: "steps"; steps: HelpStep[] }
  | { kind: "terms"; heading?: string; terms: HelpTerm[] }
  | { kind: "list"; heading?: string; items: string[] }
  | { kind: "note"; tone: "info" | "warning"; title: string; text: string }
  /** A link to another page of the workbench, e.g. "Open rules and countries". */
  | { kind: "link"; text: string; to: string }
  /** The icon legend of the draft table, drawn with the real icons. */
  | { kind: "legend"; heading: string; items: HelpLegendItem[] }
  /** A figure of src/components/figures (how it works, the queue row), with an optional caption. */
  | { kind: "figure"; figure: FigureName; caption?: string };

/**
 * The legend as the help draws it: the sentence of each LEGEND line, with the icons it
 * names in the order it names them. The tones are the ones the table uses.
 */
const ICON_LEGEND: HelpLegendItem[] = [
  { icons: [{ icon: STATUS_ICON.matched, tone: STATUS_TONE.matched }], text: LEGEND[0]!.meaning },
  { icons: [{ icon: STATUS_ICON.confirm, tone: STATUS_TONE.confirm }], text: LEGEND[1]!.meaning },
  { icons: [{ icon: STATUS_ICON.pick, tone: STATUS_TONE.pick }], text: LEGEND[2]!.meaning },
  { icons: [{ icon: STATUS_ICON["no item"] }], text: LEGEND[3]!.meaning },
  {
    icons: [
      { icon: SOURCE_ICON.mail },
      { icon: SOURCE_ICON.attachment },
      { icon: SOURCE_ICON.photo },
      { icon: SOURCE_ICON.scan },
    ],
    text: LEGEND[4]!.meaning,
  },
  {
    icons: [
      { icon: BASIS_ICON.exact_number },
      { icon: BASIS_ICON.alias },
      { icon: BASIS_ICON.earlier_mail },
      { icon: BASIS_ICON.similar },
      { icon: BASIS_ICON.person },
    ],
    text: LEGEND[5]!.meaning,
  },
  {
    icons: [
      { icon: PRICE_ICON.in_band, tone: "success" },
      { icon: PRICE_ICON.below_margin, tone: "warning" },
      { icon: PRICE_ICON.none },
    ],
    text: LEGEND[6]!.meaning,
  },
  {
    icons: [
      { icon: LEAD_ICON.known },
      { icon: LEAD_ICON.historic },
      { icon: LEAD_ICON.none },
      { icon: LEAD_ICON.not_looked_up },
    ],
    text: LEGEND[7]!.meaning,
  },
  {
    icons: [{ icon: QTY_ISSUE_ICON, tone: "warning" }, { icon: QTY_DEFAULT_ICON }],
    text: LEGEND[8]!.meaning,
  },
  {
    icons: [
      { icon: QUEUE_ICON.Confirm },
      { icon: QUEUE_ICON.Pick },
      { icon: QUEUE_ICON.Check },
      { icon: QUEUE_ICON["Copy reply"] },
    ],
    text: LEGEND[9]!.meaning,
  },
];

/** The customer line's icon, as the mail card draws it. */
const CUSTOMER_LEGEND: HelpLegendItem[] = [
  {
    icons: [{ icon: CUSTOMER_ICON.certain, tone: "success" }],
    text: 'Person with a check: "Confirmed". I2Q is sure of the customer. "Confirmed by sales": a person confirmed the customer.',
  },
  {
    icons: [{ icon: CUSTOMER_ICON.suggested, tone: "accent" }],
    text: 'Person with a magnifier: a suggestion. "Confirm" with the name is one click after reading "Why"; "Not them" rejects it.',
  },
  {
    icons: [{ icon: CUSTOMER_ICON.none }],
    text: 'Person with a cross: nobody matched. "Find customer" opens the search.',
  },
];

export interface HelpTopic {
  id: HelpTopicId;
  title: string;
  /** One or two sentences: shown in the topic index and in the small help pop-up. */
  summary: string;
  /** Extra words the search on the Help page should find. */
  keywords: string[];
  blocks: HelpBlock[];
  /** The exact screen texts this topic quotes (checked against the source by a test). */
  labels: string[];
}

export const HELP_TOPICS: readonly HelpTopic[] = [
  {
    id: "training",
    title: "Training: learn I2Q in 20 minutes",
    summary:
      "The Training page teaches I2Q in about 20 minutes, on practice cases with invented data. New to I2Q? Start with the 5-minute quick start. The training sends nothing.",
    keywords: [
      "training",
      "course",
      "learn",
      "onboarding",
      "new",
      "quick start",
      "tutorial",
      "practice",
      "certificate",
      "quiz",
    ],
    blocks: [
      {
        kind: "text",
        text: "Ten short modules show each job on the real screens, with invented cases. They cover the queue, the customer, the lines, the prices, the reply, the statuses and feedback. You pass the check at the end with 80 %.",
      },
      { kind: "link", text: "Start the training", to: "/training" },
      { kind: "link", text: "Take the 5-minute quick start", to: "/training/quick-start" },
    ],
    labels: ["Training"],
  },
  {
    id: "what-it-is",
    title: "What Norican I2Q is for",
    summary:
      "Norican I2Q (Inquiry to Quote) has one purpose: it turns customer inquiries into drafts for you to check. It runs in shadow mode: nothing is sent and nothing is written to F&O.",
    keywords: [
      "i2q",
      "inquiry to quote",
      "purpose",
      "scope",
      "inquiry",
      "purchase order",
      "shadow",
      "Go Autonomous",
      "goa",
      "read-only",
      "safe",
      "intro",
      "overview",
    ],
    blocks: [
      {
        kind: "text",
        text: "Norican I2Q has one job: customer inquiries to quotations. An inquiry is a mail in which a customer asks for a price or a quotation. I2Q reads each inquiry in the shared spare parts and sales mailboxes. It finds the customer, the item for each line and the price. Then it prepares the draft, the reply and a quotation preview for you to check.",
      },
      {
        kind: "list",
        heading: "What Norican I2Q does not work on",
        items: [
          "Purchase orders, order status questions, invoices, delivery changes, complaints and other mail.",
          'I2Q still reads and counts these mails, so we know how many there are. Purchase orders show under the tab "Purchase orders (parked)", the other mails under "Not an inquiry". None of them get a customer, lines, prices, a reply or a quotation. Handle them as today.',
        ],
      },
      {
        kind: "text",
        text: "Everything I2Q proposes comes with the reason, so you can see why.",
      },
      {
        kind: "note",
        tone: "info",
        title: "Shadow mode: read-only, nothing is sent",
        text: "I2Q runs next to Go Autonomous and next to your normal way of working. It sends no mail, makes no Outlook draft and moves no mail. It writes nothing to F&O, our price data or Go Autonomous. You reply to the customer and create the quotation exactly as you do today. The badge at the top of every page reminds you of this.",
      },
      {
        kind: "text",
        text: "Why shadow mode: we compare what I2Q proposed with what you really sent. That tells us, case by case, how often I2Q is right before anyone relies on it.",
      },
    ],
    labels: [
      "Shadow mode: read-only, nothing is sent",
      "Not an inquiry",
      "Purchase orders (parked)",
    ],
  },
  {
    id: "mail-to-quotation",
    title: "How an inquiry becomes a quotation",
    summary:
      "Nine steps from the inquiry in the shared mailbox to a quotation that you decide on. You stay in control at every step.",
    keywords: ["flow", "process", "steps", "how it works", "rfq", "inquiry"],
    blocks: [
      { kind: "figure", figure: "how-it-works" },
      {
        kind: "steps",
        steps: [
          {
            title: "A mail arrives",
            text: "A customer, an agent, a portal or a Norican colleague writes to a shared mailbox. I2Q picks the mail up within a few minutes. It reads the mail text and the attached files (PDF, Word, Excel).",
          },
          {
            title: "It becomes a case",
            text: 'Each mail gets a case with its own number (for example RFQ-d201e62acf0e). I2Q links a reply in the same thread to the case. It marks a copy of a mail that is already a case as a copy. A Norican colleague can reply inside the thread of an open case, for example with the item number of an open line. When this is switched on, I2Q files that reply on the case and makes no new case. The mail card says "replied in this thread". Its item numbers are candidates on the open line, never accepted. "Open the reply" shows the mail.',
          },
          {
            title: "Is it an inquiry?",
            text: 'I2Q decides whether the mail is an inquiry or something else (a purchase order, an invoice, a delivery question, a newsletter). Only an inquiry goes on. I2Q counts a purchase order and lists it under the tab "Purchase orders (parked)". Everything else goes to the tab "Not an inquiry".',
          },
          {
            title: "Who is the customer?",
            text: 'I2Q looks for the customer account in F&O. It uses earlier documents for this sender, contacts on earlier quotations, a quotation number in the mail and, for Norican companies, the intercompany account. The result is either certain or a suggestion for you to confirm. See "How the customer is found".',
          },
          {
            title: "Lines and items",
            text: "I2Q matches each line the customer asks for (item number, description, quantity) to one of our items. It matches an exact item number directly. Otherwise it proposes the closest items and shows why. While the customer is still a suggestion, I2Q looks up exact item numbers only. The rest follows once the customer is certain or you confirmed it.",
          },
          {
            title: "Prices and margin check",
            text: "When the customer is certain or you confirmed it, I2Q fills in the list price of the price group of the customer from F&O data. Where it knows the cost, it compares the contribution ratio with the margin limit of the area. A price below the limit needs approval.",
          },
          {
            title: "The reply",
            text: "I2Q writes the reply in the language of the customer: the lines, the prices and what is missing. You copy it into Outlook. I2Q sends nothing.",
          },
          {
            title: "Prepared quotation",
            text: "I2Q prepares the quotation as F&O would create it: header, customer and lines. This is a preview only. The write to F&O is switched off.",
          },
          {
            title: "You decide",
            text: "You confirm or change the customer and each line. Then you reply to the customer and create the quotation as today. I2Q stores your decisions with the case. We compare them with what I2Q proposed.",
          },
        ],
      },
    ],
    labels: [
      "Not an inquiry",
      "Purchase orders (parked)",
      "replied in this thread",
      "Open the reply",
    ],
  },
  {
    id: "customer",
    title: "How the customer is found",
    summary:
      'The customer line sits in the mail card at the top of a case, under the sender. It says "Confirmed" when I2Q is sure. It says "Confirmed by sales" when a person confirmed the account. It offers "Confirm" with the name and town when the customer is a suggestion. It says "Find customer" when I2Q found no account. A suggestion always needs your click.',
    keywords: [
      "account",
      "certain",
      "suggested",
      "confirm",
      "evidence",
      "intercompany",
      "sister company",
      "agent",
      "why",
      "not them",
      "find customer",
      "deeper search",
      "source",
      "site",
    ],
    blocks: [
      {
        kind: "text",
        text: "Prices, margin and the prepared quotation all depend on the customer account. So I2Q is strict: it calls a customer certain only when the evidence leaves no doubt. Everything else is a suggestion.",
      },
      {
        kind: "terms",
        heading: "What the evidence can be",
        terms: [
          {
            term: "Known sender",
            text: "Enough earlier quotations and orders for this sender address, and all of them went to one account.",
          },
          {
            term: "Quotation named in the mail",
            text: "The mail mentions one of our quotation or order numbers, and that number belongs to one account. It counts as certain only when a second sign points to the same account. Otherwise it is a suggestion.",
          },
          {
            term: "Known sister company",
            text: "The sender is a Norican colleague, and all earlier orders of this colleague went to one intercompany account. It counts as certain only when the mail is proven to come from inside Norican: the card then shows Internal mail. A second proof, Sent through the Norican relay, is built but stays switched off until IT has checked it.",
          },
          {
            term: "Earlier documents of the sender",
            text: "Most earlier documents of this sender went to one account, but not all. This is a suggestion.",
          },
          {
            term: "Earlier documents of the domain",
            text: "Other people at the same company domain were quoted on this account. This is a suggestion.",
          },
          {
            term: "Earlier documents of the agent",
            text: "An agent wrote for an end customer, and the agent's earlier documents point to one account. This is a suggestion.",
          },
          {
            term: "End customer named by the agent or trader",
            text: 'An agent, a trader or a distributor names the firm that gets the items. I2Q shows both and marks one with "Quoted first". First comes an account of the agent for this end customer, then what sales quoted on the earlier mails of the sender. Else India and the UK put the agent first, and every other entity the end customer. The end customer is certain only when the mail names it and I2Q quoted it for this sender before.',
          },
          {
            term: "End customer named, no account found",
            text: 'The mail names an end customer, but I2Q found no account for it. The open point says "I2Q found no account in F&O for the end customer.". Find the account of the end customer, or quote the agent where the agent is quoted first.',
          },
          {
            term: "Initials or F&O search name match",
            text: "The mail names the firm by its initials or by the search name it has in F&O. This is a suggestion, never one click on its own.",
          },
          {
            term: "Account number written in the mail",
            text: "The mail writes an account number of this company, for example in a price instruction. This is a suggestion. Check it against the mail.",
          },
          {
            term: "Delivery address",
            text: "The postcode of the delivery address in the mail matches one account. This is a suggestion.",
          },
          {
            term: "Site named by its city",
            text: "A firm has several accounts with the same name, one for each site. The mail names the city of one of them. This is a suggestion.",
          },
        ],
      },
      {
        kind: "terms",
        heading: "The deeper search",
        terms: [
          {
            term: "Suggested by the deeper search, with its source",
            text: "After the normal draft, the deeper search reads the customer data and the earlier quotations again. It follows one of our quotation numbers, a postcode or the items of the mail to an account. The newer form of the search adds a second check that tries to prove the result wrong. Then only a result that holds becomes a suggestion. It is never certain: you confirm it.",
          },
          {
            term: "Check before quoting",
            text: 'A card in the customer section of the "Details" sheet. It shows the open question of the deeper search and the sources that it read.',
          },
          {
            term: "Source",
            text: "A small text under an account or an item. It names what the deeper search read, with the page, the sheet or the row. Open that document and compare.",
          },
        ],
      },
      {
        kind: "note",
        tone: "info",
        title: "The deeper search shows nothing to sales people today",
        text: "Where the deeper search runs today, it runs in a test mode that changes no case. It starts for one company at a time, after a test on earlier cases. It never confirms a customer and never accepts a row. It can also suggest an item for a row that says pick.",
      },
      {
        kind: "terms",
        heading: "What you see on the customer line",
        terms: [
          {
            term: "Confirmed",
            text: "I2Q is sure of this account. Prices and rows use it.",
          },
          {
            term: "Confirmed by sales",
            text: "A sales person picked or confirmed this account on this case. Prices and rows use it. Undo is in the customer menu.",
          },
          {
            term: "Confirm",
            text: 'The button with the name and town, for example "Confirm Example Foundry Ltd, Leeds". A likely account, with the evidence one click away. It can be wrong. The big button under the draft says the same until you decide.',
          },
          {
            term: "Why",
            text: 'Opens the evidence behind the proposal: the earlier documents, contacts and addresses, and up to three other accounts that could fit. At the end of it sits "Find customer".',
          },
          {
            term: "Not them",
            text: "The suggestion is wrong. One click opens the search by name or account number.",
          },
          {
            term: "Find customer",
            text: 'I2Q found no account for the sender, or you rejected the suggestion. It opens the search by name or account number. The queue row says "not identified" under the customer until then.',
          },
          {
            term: "Change",
            text: 'The menu at the end of the line. On a confirmed customer it says "Change" and holds "Change customer": the same search. On a suggestion it is three dots with "Change customer" and "Not them". After a change, I2Q reads the prices again for the account you picked.',
          },
        ],
      },
      {
        kind: "list",
        heading: "What to do with a suggestion",
        items: [
          'Read the mail. Open "Why". If the evidence fits, click "Confirm". Your first click on a case also takes the case for you.',
          'If it does not fit, click "Not them" and search by name or account number. With two accounts that fit, the big button says "Pick the customer" and opens the same search.',
          'Clicked by mistake? The link "Undo" in the customer section of the "Details" sheet takes it back.',
          "After you confirm the customer, I2Q reads the case again. The prices for this customer appear on the rows.",
        ],
      },
      {
        kind: "note",
        tone: "info",
        title: "How reliable is it?",
        text: "We measured this on 1,004 earlier cases (9 Oct 2026). The quotation that sales sent shows the right account. Of 522 customers that I2Q called certain, 5 were wrong. Of 448 suggestions, 91 were wrong. That is why a suggestion always waits for you.",
      },
    ],
    labels: [
      "Known sender",
      "Quotation named in the mail",
      "Known sister company",
      "Earlier documents of the sender",
      "Earlier documents of the domain",
      "Delivery address",
      "Internal mail",
      "Sent through the Norican relay",
      "Confirmed",
      "Confirmed by sales",
      "Confirm",
      "Why",
      "Not them",
      "Find customer",
      "Pick the customer",
      "not identified",
      "Earlier documents of the agent",
      "Details",
      "Undo",
      "Change",
      "Change customer",
      "Site named by its city",
      "Suggested by the deeper search, with its source",
      "Check before quoting",
    ],
  },
  {
    id: "case-page",
    title: "The case page: read the mail, check the draft, do the one action",
    summary:
      "One inquiry in three steps, and nothing else expanded. The mail is a compact card at the top. The draft is a table in the middle, with an icon and a word per row. One big button in the bar at the bottom names the next step. Everything else sits under More, Why, the chevron of a row or the Details sheet.",
    keywords: [
      "case",
      "mail",
      "draft",
      "table",
      "rows",
      "lines",
      "icons",
      "legend",
      "pick",
      "confirm",
      "action",
      "button",
      "more",
      "details",
      "sheet",
      "reply",
      "quotation",
      "margin",
      "price",
      "currency",
      "held",
    ],
    blocks: [
      {
        kind: "steps",
        steps: [
          {
            title: "Read the mail",
            text: 'The mail is a compact card at the top of the page, full width. It shows who wrote, the language with "Translate", the customer line, the attachments as chips with their icon, and the own words of the customer. Long text is folded to 6 lines with "Read all". Earlier mails of the thread are folded under "Show earlier mails". A follow-up, a reminder or "Same inquiry as" another case is one line under the subject. The customer line in the card says "Confirmed", "Confirmed by sales", "Confirm" with the name, or "Find customer", each with its icon. See "How the customer is found". In Outlook the pane shows no mail card. Outlook shows the mail itself.',
          },
          {
            title: "Check the draft",
            text: 'The draft is a table in the middle of the page, one row per line, in six columns. Status shows an icon and a word. Customer asked shows the words of the customer, with an icon for their source. I2Q proposes shows the item number, its name and an icon for how I2Q found it. Qty, Price (with its band icon) and Lead (with its icon) carry the figures. The rows come in three groups, in this order: "Needs you" (rows that say "pick" or "confirm"), "Matched" (I2Q is sure, or you decided), and "Nothing to quote" (rows with "no item", folded). The word "matched" means: I2Q is sure. The big button "Accept" takes these rows in one click; a row you or a colleague decided needs nothing more. The word "confirm" means: a good guess, press "Confirm" at the right end of the row. The word "pick" means: no sure item, press "Pick item" at the right end of the row and pick one from the list. The word "no item" means: somebody said there is no item for this line. The strip above the table says how many rows have an item and how many lines the customer removed. It also shows the count per group, with its icon. To change a quantity, click it in the Qty cell, type a whole number and press Enter. Tab goes to the next row. "Set quantity for all lines" in the strip sets one quantity on many rows. Click the words of the customer in a row and the mail card marks them. Click the status word and the row opens its details. A person with a check on a price means: you or a colleague set it. Set price, Change price and Undo price are in the row menu (the three dots).',
          },
          {
            title: "Do the one action",
            text: 'One big button in the bar at the bottom of the page names the next step. It changes as you work: "Confirm", "Confirm 5 items", "Pick 2 items", "Accept 1 matched item", "Check", "Prepare the reply", "Copy the reply", "Mark done and next". The button "Nothing to do" means exactly that: press it and go to the next case. The sentence next to the button says what stands between the case and a reply. Click the sentence and the page goes to the control. "Open points" with a number lists everything that is open. See "When a case does not go through". When a reply exists, its text sits in a card above the bar with its subject and the gaps to fill.',
          },
        ],
      },
      { kind: "legend", heading: "What the icons mean", items: ICON_LEGEND },
      { kind: "legend", heading: "The customer line", items: CUSTOMER_LEGEND },
      {
        kind: "terms",
        heading: "Where the rest sits",
        terms: [
          {
            term: "Details",
            text: 'Behind the chevron on the status word of a row. It shows why I2Q picked the item, the other candidates, and the price with its band and history. It shows the quantity I2Q read with "Correct" beside it, then "No item" or "Undo", and "Was this right? Give feedback". "Change item" on a matched or confirm row sits in the row menu (the three dots), with "Set price". A matched row nobody decided also shows "Change item" behind the chevron. "Correct quantity" is in the row menu too. The switch "Show details" in the strip above the table shows the long figures in the cells on a laptop. On a phone or in Outlook it opens every row at once. The long figures are the list price, the band, the lead time source and the order value under the table. This browser remembers the setting.',
          },
          {
            term: "Set price",
            text: 'In the row menu (the three dots) of a row with an item. It opens a small dialog with three boxes: the price per unit, the currency and a note for colleagues. The currency has three letters, like EUR or GBP. The box starts with the currency of the case. The row then shows your price with a person and a check. "Change price" opens the same dialog with your price in it. "Undo price" takes it off and shows the list price again.',
          },
          {
            term: "Quantity",
            text: 'Click the quantity of a row, type a whole number and press Enter. Tab saves and goes to the next row. Escape leaves the old value. A small mark says "Corrected by sales". "Set quantity for all lines" sets one quantity on the rows without a given quantity, on a group, or on all rows. A reply can name one item without a quantity while the earlier mail of the customer asks for 2 of the same item. The row then takes 2 with the mark "Quantity from an earlier mail", and its details show the sentence it came from. Check it before you send. When the mail gives no quantity for a line, I2Q uses 1. The cell shows 1 with an asterisk: "Quantity not given, 1 used". The price is for 1, and the reply asks the customer to confirm the quantity. Type the real quantity when you know it. "Correct quantity" in the row menu opens the dialog with the unit and a note.',
          },
          {
            term: "Named in the earlier mails",
            text: 'Under the rows. The newest mail asks for its own items, and an earlier mail of the thread names other items. I2Q lists those other items here, with the item number and the quantity when it knows them. They are not in the draft. Add a line with "Add line" only when the customer asks for them now. A row that I2Q read in a forwarded mail carries the source "From an earlier mail of the thread".',
          },
          {
            term: "Open points",
            text: 'The button next to the sentence in the bar, with a number. It lists what stands between the case and a reply under "Before you can prepare the reply". Under "Open in the reply if you leave them" it lists what the reply then asks the customer. Each line is a link, and most have one button.',
          },
          {
            term: "Details (n)",
            text: 'The sheet, next to the big button. The number counts the checks that hold the case and the open questions. Its first sections are the checks, the customer with the evidence and the machines, and the quotation preview. Then come the order system with "Prepare quotation", the documents and photos, the related mails and quotations, the history, and feedback. A manager approves a held price with "Approve price". It is in the row menu of the held row, on the big button and under "More". "Prepare quotation" lists the approved prices with "Take back". The sheet opens closed on every case. It remembers which sections you opened.',
          },
          {
            term: "Quoted in F&O",
            text: 'In the Details sheet, section "Order system", when I2Q linked the inquiry to a quotation in F&O. It shows the quotation, the sales order and the lines of the draft against the lines that sales quoted. When the quotation became a sales order, each quoted line has a tag. "Ordered": the sales order has this line. "Removed": the sales order does not have this line. "Cancelled": the line is cancelled on the sales order. "Quantity changed" shows the quoted and the ordered quantity. "Added on the sales order" lists the items that the quotation did not have. A credit line never counts as ordered. Until I2Q reads the sales order lines, the tags compare the lines of the quotation at the order, and the block says so.',
          },
          {
            term: "More",
            text: 'The menu holds "Take", "Take over" or "Release", and "Set status" with "Mark done", "Not for us", "Waiting for customer" and "Reopen". It also holds "Give feedback", "Shortcuts", and what the current step allows, such as "Edit text", "Undo my edits", "Not for us" or "Next case".',
          },
          {
            term: "Header",
            text: 'Back to the queue, the subject, "3 of 12" with previous and next when you came from the queue, and the help button.',
          },
        ],
      },
      {
        kind: "terms",
        heading: "Marks on a row and on the case",
        terms: [
          {
            term: "Only in UK: 4711",
            text: "The number is not in the catalogue of this case's company, but another company has it. A hint only: ask that company, or pick an item of your own catalogue.",
          },
          {
            term: "HSN 84749000",
            text: 'India only: the HSN code of the item in F&O. "HSN missing", "HSN placeholder" and "HSN not valid" say what is wrong with it. Ask the master data team to correct it.',
          },
          {
            term: "Size differs",
            text: 'The item does not fit the row: "Size differs", "Description differs", "Customer says wrong part" or "Whole assembly for a position". Check the row alone. The bulk confirm skips it.',
          },
          {
            term: "US number form",
            text: "The customer wrote 97703 and the catalogue has 0977030. Confirm the item before you quote it.",
          },
          {
            term: "Customer asks about price on SQ0099001",
            text: "A price question or a discount request on our quotation. It is a follow-up on that quotation, not a new inquiry. The case gets no new lines. Reply from the quotation.",
          },
          {
            term: "From our quotation SQ0990456",
            text: "The customer orders on our quotation or sends it back signed. Each line is a position of that quotation, with the change the mail asks for. A position that the order leaves out shows as removed and is not quoted. Every line is a suggestion. Check the lines against the order.",
          },
          {
            term: "Delivery terms differ",
            text: "The mail names other delivery terms than the customer in F&O. The note is in the prepared quotation. Agree the terms with the customer before you quote.",
          },
          {
            term: "Same name and address",
            text: "F&O has two accounts with the same name and address. I2Q proposes none of them. Pick the right account. The button names the account number.",
          },
          {
            term: "Your decision on a line that I2Q no longer reads",
            text: 'I2Q read the mail again and found no line for an earlier decision. The decision stays in "Earlier decisions" under Details. Then decide the line that I2Q read.',
          },
          {
            term: "Close: not an inquiry",
            text: 'A system notification or a newsletter. One click sets the case to "Not for us". I2Q does not close it by itself.',
          },
          {
            term: "Quoted first",
            text: "On the mail of an agent, the party whose account you quote. The other party stays on the page.",
          },
          {
            term: "Show all 112 lines",
            text: "A long list keeps all its rows. The draft names the source of each group of rows, and a group of more than 50 rows starts folded.",
          },
        ],
      },
      {
        kind: "terms",
        heading: "What the big button can say",
        terms: [
          { term: "Reading the mail", text: "I2Q has not finished with this mail. Wait a moment." },
          {
            term: "Reading again",
            text: "I2Q reads a case again that it had read before, for example after you confirmed the customer. This can take up to 5 minutes. The page shows the result by itself. The rows keep their buttons.",
          },
          {
            term: "Set status",
            text: 'I2Q could not read this mail. Handle it by hand. The menu More holds "Try again".',
          },
          {
            term: "Nothing to do",
            text: "Closed, quoted already, the same inquiry as another case, or not an inquiry. The caption says which. Press it to go to the next case.",
          },
          {
            term: "Take over",
            text: "A colleague has this case. The caption names the colleague.",
          },
          {
            term: "Find customer",
            text: 'I2Q found no account for the sender. The button says "Pick the customer" when two accounts fit.',
          },
          {
            term: "Confirm 5 items",
            text: "Every open row is a suggestion with an item. One click confirms them all. Check each item first: a confirmed item goes into the reply.",
          },
          {
            term: "Pick 2 items",
            text: "Two rows have no item. The button goes to the first of them. Pick an item there, or press No item.",
          },
          {
            term: "Accept 1 matched item",
            text: "Every row is matched or decided. One click confirms the matched rows.",
          },
          {
            term: "Approve the price of line 2",
            text: "For a manager: the price of this row is below the limit. One click approves it. The row is not held any more.",
          },
          {
            term: "Check",
            text: 'A rule holds the case. The button names the rule after "Check": "Price approval needed", "Held: no price", or the rule in words, for example "Competitor on the sender list" or "HSN missing". The sentence next to the button says the same and what to do. "Held by a rule" stands for a rule without words of its own. The button goes to the held row. For a rule without a row it opens the sheet at the checks.',
          },
          {
            term: "Held by a rule",
            text: 'A check without a name of its own holds the case. Open the sheet with the button and read the reason under "Checks".',
          },
          {
            term: "Prepare the reply",
            text: 'No reply yet. The button says "Prepare again" when the reply was "Made before your changes".',
          },
          {
            term: "Copy the reply",
            text: "The subject and the text go to your clipboard, with the gaps to fill in square brackets. Nothing is sent from here. Copy the reply and send it from Outlook.",
          },
          {
            term: "Mark done and next",
            text: "You copied the reply. It closes the case and opens the next one.",
          },
        ],
      },
      {
        kind: "text",
        text: 'The first write on a case that nobody has takes the case for you. A write is a confirm, a pick, a quantity, a price, an accept or a prepare. A toast says "The case is yours now." You do not have to release a case to see a change. The menu More holds "Take" on its own. A mail under "Not an inquiry" shows only the mail and "Nothing to do". It gets no customer, rows, prices, reply or quotation.',
      },
    ],
    labels: [
      "Quoted in F&O",
      "Ordered",
      "Removed",
      "Cancelled",
      "Quantity changed",
      "Added on the sales order",
      "Translate",
      "Show earlier mails",
      "Same inquiry as",
      "Confirmed",
      "Confirmed by sales",
      "Confirm",
      "Find customer",
      "Correct quantity",
      "Set quantity for all lines",
      "Quantity not given, 1 used",
      "Corrected by sales",
      "Open points",
      "Before you can prepare the reply",
      "Open in the reply if you leave them",
      "The case is yours now.",
      "Take back",
      "matched",
      "confirm",
      "pick",
      "no item",
      "Pick item",
      "Nothing to do",
      "Reading the mail",
      "Reading again",
      "Set status",
      "Try again",
      "Take over",
      "Pick the customer",
      "Check",
      "Competitor on the sender list",
      "HSN missing",
      "Prepare the reply",
      "Prepare again",
      "Made before your changes",
      "Copy the reply",
      "Mark done and next",
      "Nothing is sent from here. Copy the reply and send it from Outlook.",
      "Details",
      "Correct",
      "No item",
      "Undo",
      "Was this right? Give feedback",
      "Show details",
      "Read all",
      "Needs you",
      "Matched",
      "Nothing to quote",
      "Change item",
      "Set price",
      "Change price",
      "Undo price",
      "Price approval needed",
      "Held: no price",
      "Held by a rule",
      "Checks",
      "Approve price",
      "Prepare quotation",
      "More",
      "Take",
      "Release",
      "Mark done",
      "Not for us",
      "Waiting for customer",
      "Reopen",
      "Give feedback",
      "Shortcuts",
      "Edit text",
      "Undo my edits",
      "Next case",
      "Not an inquiry",
      "Named in the earlier mails",
      "Add line",
      "From an earlier mail of the thread",
      "Quantity from an earlier mail",
      "HSN placeholder",
      "HSN not valid",
      "Size differs",
      "Description differs",
      "Customer says wrong part",
      "Whole assembly for a position",
      "US number form",
      "Close: not an inquiry",
      "Quoted first",
    ],
  },
  {
    id: "case-does-not-go-through",
    title: "When a case does not go through",
    summary:
      "The sentence next to the big button names the one thing that stands between the case and a reply. Click it. When the data is not in F&O, set the value by hand or set the status of the case.",
    keywords: [
      "stuck",
      "blocked",
      "held",
      "cannot",
      "open points",
      "quantity",
      "price",
      "customer",
      "data gap",
      "approve",
      "release",
    ],
    blocks: [
      {
        kind: "list",
        heading: "Read the sentence, then click it",
        items: [
          "The sentence next to the big button names the one thing that stands between the case and a reply.",
          "Click the sentence. The page goes to the control and marks it.",
          '"Open points" with a number lists everything that is open. Each line is a link.',
        ],
      },
      {
        kind: "terms",
        heading: "What the sentence says, and what you do",
        terms: [
          {
            term: "No customer account: find the customer.",
            text: "I2Q found no account for the sender. Click the sentence and search by name or account number.",
          },
          {
            term: "Customer to confirm: pick the customer.",
            text: "I2Q suggests an account and is not sure. Check it against the mail. Confirm it, or pick another account.",
          },
          {
            term: "No lines found in the mail: add a line, or prepare a reply that asks the customer.",
            text: 'I2Q read no line to quote. Add a line with "Add line", or prepare a reply that asks the customer what they need.',
          },
          {
            term: "2 lines have no item: pick an item or press No item.",
            text: 'The sentence goes to the first of these rows. Press "Pick item" and pick one. Press "No item" when there is no item for the line.',
          },
          {
            term: "5 lines to confirm: check the items, then confirm.",
            text: "Each row is a suggestion. Check the items against the mail. The big button then confirms them all in one click.",
          },
          {
            term: "I2Q is sure of 3 lines: accept them.",
            text: "The big button accepts these rows in one click.",
          },
          {
            term: "Line 2 is held: the price is below the limit. Approve the price or change it.",
            text: 'For a manager. Press "Approve price", or set a higher price with "Set price". The row is not held any more.',
          },
          {
            term: "Line 2 is held: the price is below the limit. Change the price, or ask a manager to approve it.",
            text: 'For everyone else. Set a higher price with "Set price", or ask a manager to open the case and approve the price.',
          },
          {
            term: "The price approval of line 2 no longer matches: the price or the quantity changed. Approve the price again.",
            text: "The row changed after the approval. A manager approves the new price.",
          },
          {
            term: "Held by a rule",
            text: "Another rule holds the case. The sentence gives the rule, the reason and what to do next.",
          },
          {
            term: "No quantity on 5 lines: set the quantity.",
            text: 'An open point: you can prepare the reply, and it then asks the customer for the quantity. To set it, type in the Qty cell or press "Set quantity for all lines". When the mail gives no quantity for a line, I2Q uses 1 and marks it "Quantity not given, 1 used". This point lists the other lines, for example a reference line.',
          },
          {
            term: "Quantity or unit to check on 1 line: correct the quantity.",
            text: "An open point. I2Q is not sure of the quantity or the unit. Correct it, or the reply says quantity and unit to confirm.",
          },
          {
            term: "3 lines have no price: set a price or press No item.",
            text: 'An open point. Set the price with "Set price", or the reply says price to confirm.',
          },
        ],
      },
      {
        kind: "note",
        tone: "info",
        title: "When the data is not in F&O",
        text: 'When I2Q finds no account, no item or no valid price in F&O, it cannot finish the case by itself. The page then says "I2Q cannot finish this case by itself." Set the value by hand, or set the status "Not for us" or "Waiting for customer" with one click. The page says this for a case without an account, and when no line has an item or no line has a valid price. When only some lines have no item or no price, the sentence names the step for those lines.',
      },
      {
        kind: "text",
        text: 'While a colleague has the case, the page does not offer "Set quantity for all lines" or a button that confirms many rows. Press "Take over" first. You can still correct one row.',
      },
      {
        kind: "text",
        text: "You do not have to take or release a case to see a change. The first save takes the case. The page reads the case again by itself.",
      },
      {
        kind: "text",
        text: 'A held price: a manager presses "Approve price". The approval stays until the item, the price, the quantity or the customer of that line changes. The page then says so.',
      },
    ],
    labels: [
      "Open points",
      "No customer account: find the customer.",
      "Customer to confirm: pick the customer.",
      "No lines found in the mail: add a line, or prepare a reply that asks the customer.",
      "Add line",
      "Pick item",
      "No item",
      "Approve price",
      "Set price",
      "Held by a rule",
      "Set quantity for all lines",
      "Quantity not given, 1 used",
      "I2Q cannot finish this case by itself.",
      "Not for us",
      "Waiting for customer",
      "Take over",
    ],
  },
  {
    id: "work-faster",
    title: "Work faster: fewer clicks per inquiry",
    summary:
      "The case page shows one big button that names the next step. One click accepts the matched rows. The next case is one click or one key away. The queue changes many cases at once.",
    keywords: [
      "fast",
      "faster",
      "clicks",
      "keyboard",
      "shortcut",
      "keys",
      "next",
      "bulk",
      "select",
      "accept",
      "backlog",
      "copy",
    ],
    blocks: [
      {
        kind: "steps",
        steps: [
          {
            title: "Confirm the customer in one click",
            text: 'The customer line offers "Confirm" with the suggested customer and town. The big button says the same. After the click, I2Q matches the rows again for that customer. This can take up to 5 minutes. The page shows the result by itself. The case is also under "Back for you" when that is done.',
          },
          {
            title: "Let the big button lead",
            text: 'It names the next open step with its button: "Confirm 5 items" confirms every suggested row, "Pick 2 items" goes to the first row without an item, "Check" goes to the held row, "Prepare the reply" writes the reply. You never look for the next thing to do.',
          },
          {
            title: "Set the quantities without a dialog",
            text: 'Click a quantity, type, press Enter. Tab goes to the next row. For many rows, press "Set quantity for all lines" in the strip above the table.',
          },
          {
            title: "Accept the matched rows in one click",
            text: 'The button "Accept 1 matched item" appears after you decide every other row. It confirms every row that I2Q matched and nobody decided yet. Each row keeps its own "Undo" in its Details.',
          },
          {
            title: "Copy the reply",
            text: 'The button "Copy the reply" puts the subject and the text on the clipboard. Paste it into your reply in Outlook and send it from there.',
          },
          {
            title: "Close and go on",
            text: 'The button "Mark done and next" closes the case and opens the next case of the queue. On a closed or quoted case, "Nothing to do" does the same without a change.',
          },
        ],
      },
      {
        kind: "terms",
        heading: "Keyboard shortcuts",
        terms: [
          {
            term: "j / k",
            text: "Next / previous case on the case page; next / previous row in the queue.",
          },
          { term: "a", text: "Accept the matched rows." },
          { term: "c", text: "Confirm the suggested customer." },
          { term: "t", text: "Take the case." },
          { term: "d", text: "Mark done and next." },
          { term: "r", text: "Copy the reply." },
          {
            term: "Arrow up / Arrow down",
            text: "Move in the draft, when the focus is in the draft.",
          },
          {
            term: "Enter (draft)",
            text: "Confirm the row when it is a suggestion. Open Pick item when the row has no item.",
          },
          { term: "n", text: "No item for the row." },
          { term: "p", text: "Set the price of the row." },
          { term: "q", text: "Set the quantity of the row." },
          { term: "i", text: "Pick an item for the row." },
          { term: "Enter", text: "Open the row (queue)." },
          { term: "x", text: "Tick the row (queue)." },
          {
            term: "?",
            text: 'The list of keys, also under More as "Shortcuts", with "Use keyboard shortcuts" to switch them off in this browser. The keys do nothing while you type in a field.',
          },
        ],
      },
      {
        kind: "terms",
        heading: "Many cases at once (queue)",
        terms: [
          {
            // ste-allow: screen-label
            term: "Select all on this page",
            text: 'Press x on a row, or tick rows. Then tick the box above the list. The bar at the bottom offers "Mark done", "Not for us", "Waiting for customer" and "Take". A close asks first. You can reopen each case on its own.',
          },
          {
            term: "Changed by a colleague",
            text: 'I2Q leaves a case as it is when a colleague changed it after you loaded the list. The bar says how many. The link "Select the" ticks them again, so you can look at them.',
          },
          {
            term: "open older than 7 days",
            text: "The chip above the tabs opens the open inquiries received more than a week ago. With the tick box, you clear an old backlog page by page.",
          },
        ],
      },
    ],
    labels: [
      "Confirm",
      "Back for you",
      "Check",
      "Prepare the reply",
      "Set quantity for all lines",
      "Undo",
      "Copy the reply",
      "Mark done and next",
      "Nothing to do",
      "Shortcuts",
      "Use keyboard shortcuts",
      "Select all on this page",
      "Mark done",
      "Not for us",
      "Waiting for customer",
      "Take",
      "Select the",
      "open older than 7 days",
    ],
  },
  {
    // The tidied menu (15 Oct 2026): the groups, the header buttons and the icon strip of
    // components/app-shell.tsx (NAV_GROUP_LABELS, NAV_TOGGLE_LABELS).
    id: "menu",
    title: "The menu",
    summary: "The menu has groups. Open a group to see its pages, and collapse the menu to icons.",
    keywords: ["menu", "navigation", "group", "collapse", "expand", "icons", "sidebar"],
    blocks: [
      {
        kind: "list",
        heading: "The groups",
        items: [
          '"Work" is always open: "Queue", "Back for you" and "Learned corrections".',
          '"Insight", "Learn and help", "Plans and decisions" and "Feedback" open and close.',
          "A group that holds the page you are on opens by itself.",
          "You see only the groups and pages that your role may open.",
        ],
      },
      {
        kind: "list",
        heading: "Open or close a group",
        items: [
          "Click the name of the group, or move to it with Tab and press Enter.",
          "Click the name again to close the group.",
        ],
      },
      {
        kind: "list",
        heading: "Collapse the menu",
        items: [
          'Click "Collapse menu" at the bottom of the menu. The menu shows icons only.',
          "Point to an icon to see the name of its page. A dot on an icon shows news.",
          'Click "Expand menu" to show the names again.',
        ],
      },
      {
        kind: "text",
        text: "I2Q keeps the open groups and the collapsed menu in this browser. On a phone, the menu button at the top shows the same groups.",
      },
    ],
    labels: [
      "Work",
      "Insight",
      "Learn and help",
      "Plans and decisions",
      "Feedback",
      "Collapse menu",
      "Expand menu",
    ],
  },
  {
    id: "back-for-you",
    title: "Back for you",
    summary:
      'Cases that came back to you show under "Back for you" in the menu, with a count. I2Q sends no mail and no Teams message.',
    keywords: ["back", "waiting", "follow up", "notification", "badge", "attention"],
    blocks: [
      {
        kind: "list",
        heading: "A case is back for you when",
        items: [
          'You confirmed its customer, and I2Q matched the lines again for that customer. The row says "Lines matched for" and the customer.',
          'You set it to "Waiting for customer", and 5 working days passed. The row says "No reply from the customer for 5 working days". The case stays waiting until you change it.',
        ],
      },
      {
        kind: "text",
        text: 'A case leaves the list after you decide something on it or set its status. In the queue, the chip "Back for you" opens exactly this list, of any date.',
      },
    ],
    labels: [
      "Back for you",
      "Lines matched for",
      "Waiting for customer",
      "No reply from the customer for 5 working days",
    ],
  },
  {
    id: "prepared-replies",
    title: "Replies prepared by I2Q",
    summary:
      "I2Q prepares the reply as soon as an inquiry is ready, so it is there when you open the case. Timo switched this on on 10 Oct 2026.",
    keywords: ["reply", "draft", "prepared", "automatic", "arrival", "switch"],
    blocks: [
      {
        kind: "text",
        text: 'A prepared reply is there when you open the case: the text sits in a card above the bar. The big button says "Copy the reply" once every row is decided; while matched rows wait, it says "Accept n matched items" first. The queue row says "Copy reply" in its Action column. I2Q makes it the same way as when you click "Prepare the reply", with the same checks. Nothing is sent: you copy it and send it from Outlook.',
      },
      {
        kind: "text",
        text: 'The case can change afterwards: another customer, a decided or undone row, a changed quantity or price, or I2Q reads the mail again. Then the big button says "Prepare again" with the caption "Made before your changes", in I2Q and in the Outlook pane. The menu More holds "Copy anyway". If I2Q cannot read the saved replies, the page says so with "Try again". The reply may still exist.',
      },
      {
        kind: "note",
        tone: "info",
        title: "Switched on on 10 Oct 2026",
        text: "A reply that I2Q prepares without a request costs a little (about EUR 3 per 60 days). Timo decided to switch it on. The dashboard shows how many prepared replies are used.",
      },
    ],
    labels: [
      "Copy the reply",
      "Copy reply",
      "Prepare the reply",
      "Made before your changes",
      "Prepare again",
      "Copy anyway",
      "Try again",
    ],
  },
  {
    id: "i2q-decided-lines",
    title: "Lines decided by I2Q",
    summary:
      "In the UK, a line whose item number the customer wrote exactly as it is in the catalogue counts as decided by I2Q. You can undo it on the line.",
    keywords: ["decided", "exact", "number", "automatic", "undo", "uk"],
    blocks: [
      {
        kind: "text",
        text: 'Such a row says "matched" like any other, and its Details say "Decided by I2Q (exact number)". It counts as decided for the quotation check, like a row you confirmed. Only lines with an exact item number count: read from the mail or a document as text, with the item in the UK catalogue. Lines read from a picture, a scan or a spare parts list, and every suggested line, still need you.',
      },
      {
        kind: "text",
        text: 'Click "Undo" in the Details of the row to decide it yourself. From then on the row is yours, also after I2Q reads the mail again. The case history shows who took the decision back.',
      },
      {
        kind: "note",
        tone: "info",
        title: "Why only exact numbers in the UK",
        text: "We checked UK lines matched this way on earlier quotations: no error in 633 accepted lines. Still look at them. If the number the customer wrote belongs to a different item in F&O, click Undo and pick the right one. The customer still needs your confirmation. Nothing is sent or written to F&O.",
      },
    ],
    labels: ["matched", "Decided by I2Q (exact number)", "Decided by I2Q", "Undo", "Details"],
  },
  {
    id: "deeper-search",
    title: "The deeper search: proposals and Look again",
    summary:
      "For a case that I2Q left open, the deeper search can propose a customer or an item. You confirm it or you reject it. In some countries you can ask for it again.",
    keywords: [
      "deeper search",
      "proposal",
      "proposed",
      "why",
      "look again",
      "not this one",
      "provisional",
      "alternative",
      "other account",
    ],
    blocks: [
      {
        kind: "text",
        text: "The deeper search looks things up in the customer data, the item catalogue and our earlier quotations. It runs when I2Q found no customer, or when a line is still open. It proposes, and you decide. It never confirms a customer and it never accepts a line.",
      },
      {
        kind: "terms",
        heading: "On the customer line",
        terms: [
          {
            term: "Proposed by the deeper search",
            text: 'The tag on an account that the deeper search proposes. The sentence under the tag says why. "Why" beside it lists each lookup with the rows it found.',
          },
          {
            term: "Confirm",
            text: "Confirms the proposed account, like every customer confirmation. I2Q then reads the case again for this customer and reads the prices.",
          },
          {
            term: "Not this one",
            text: 'Rejects the proposal. The history of the case keeps it. The customer line then offers the next account, or "Find customer".',
          },
          {
            term: "Check this before you confirm",
            text: 'The heading of a doubt in "Why". A proposal with a doubt is never the first account on the line. Read the doubt and the lookups before you confirm.',
          },
          {
            term: "Other account",
            text: 'On a mail from an agent: the second account of the pair. It is the account of the sender, or the account of the end customer. "Use this" confirms it.',
          },
        ],
      },
      {
        kind: "terms",
        heading: "On a row",
        terms: [
          {
            term: "Proposed by the deeper search",
            text: 'Under the item of a row: the item is a proposal. Open the row to read the lookups. "Confirm" in the row confirms it, and "Not this one" in the details rejects it.',
          },
          {
            term: "Alternative",
            text: 'An item beside the item of the row, for example the replacement of the written item. The line keeps its item. "Pick item" in the details of the row changes it.',
          },
          {
            term: "Provisional",
            text: "The chip on a list price for the suggested account. The row names the account and the price group. It is no price for a quotation. Confirm the customer to get the price. The details also show the usual discount, the last quoted price and a lead time, each with its source.",
          },
        ],
      },
      {
        kind: "terms",
        heading: "Ask for the search again",
        terms: [
          {
            term: "Look again",
            text: "Above the draft lines for the whole case, and in the row menu (the three dots) for one line. The deeper search then looks at the case again. A line that you decided or corrected is left out. You can use it 3 times for one case. Wait 2 minutes between two requests.",
          },
          {
            term: "Looking again",
            text: "Shown while the request waits. The result comes on this page, so you can go on with other cases.",
          },
        ],
      },
      {
        kind: "note",
        tone: "info",
        title: "Where you see it",
        text: 'The proposals and "Look again" are switched on for each country. In a country without the switch the case page looks as before. Nothing is sent and nothing is written to F&O.',
      },
    ],
    labels: [
      "Proposed by the deeper search",
      "Why",
      "Confirm",
      "Not this one",
      "Find customer",
      "Check this before you confirm",
      "Other account",
      "Use this",
      "Alternative",
      "Pick item",
      "Provisional",
      "Look again",
      "Looking again",
    ],
  },
  {
    id: "translation",
    title: "Mails in another language",
    summary:
      "A customer mail in another language is shown translated on the case page. The original is always one click away.",
    keywords: [
      "translate",
      "translation",
      "language",
      "spanish",
      "german",
      "portuguese",
      "turkish",
      "italian",
      "czech",
      "original",
    ],
    blocks: [
      {
        kind: "text",
        text: 'When a mail is not in your language, the mail card shows the subject and the latest text of the mail translated. Above the text it says which language the mail was written in, for example "Translated from Spanish". Click "Show original" to read the mail as the customer wrote it. Click "Show translation" to go back. Earlier mails in the thread stay as written.',
      },
      {
        kind: "text",
        text: 'Your language is English unless your browser uses Danish, German, French, Spanish or Czech. Change it with "Read mails in" next to "Translate". This browser remembers your setting.',
      },
      {
        kind: "text",
        text: 'The first time somebody opens a mail in a language, I2Q translates it. The page shows "Translating..." while it works, and you can read the original in the meantime. After that I2Q stores the translation, and it opens at once for everyone. If I2Q cannot translate the mail, the page shows the original with a short note. The row Details of the draft keep the own words of the customer.',
      },
      {
        kind: "note",
        tone: "warning",
        title: "Check against the original",
        text: "I2Q keeps numbers, item numbers, prices and dates as written. If one of them is missing in the translation, the pane says so. Use the original for quantities, prices and item numbers when you prepare the quotation.",
      },
    ],
    labels: [
      "Translate",
      "Show original",
      "Show translation",
      "Read mails in",
      "Translating...",
      "Check against the original",
    ],
  },
  {
    id: "photos",
    title: "Photos and scanned pages",
    summary:
      'I2Q can read item numbers, quantities, drawing numbers and machine details from photos and scanned pages. What it reads shows under "From the photo" as evidence. I2Q accepts nothing read from a picture without your decision.',
    keywords: [
      "photo",
      "picture",
      "image",
      "scan",
      "nameplate",
      "label",
      "drawing",
      "handwritten",
      "serial",
      "heic",
      "jpeg",
      "vision",
      "ocr",
    ],
    blocks: [
      {
        kind: "text",
        text: 'I2Q reads the photos and scanned pages of an inquiry for every company. It reads nameplates, item labels, spare parts lists with marked rows, drawings and scanned forms. I2Q reads each picture twice, once whole and once in sections, and compares the two readings. The section "From the photo" in the documents of the "Details" sheet shows what it read, with "Attachments read".',
      },
      {
        kind: "terms",
        heading: "How sure a reading is",
        terms: [
          {
            term: "Agreed (two readings)",
            text: "Both readings found the same characters. It can still be wrong, so compare it with the picture.",
            system: "agreed",
          },
          {
            term: "Read once",
            text: "Only one reading found it. Check it against the picture before you use it.",
            system: "read_once",
          },
          {
            term: "Unclear",
            text: "At least one character was unreadable. I2Q does not look the number up in the catalogue.",
            system: "unclear",
          },
        ],
      },
      {
        kind: "steps",
        steps: [
          {
            title: "Look at the panel",
            text: 'Open "Details" under the big button and go to the documents. Each picture shows a small preview, what I2Q read, how sure the reading is and the row it fed under "Used for".',
          },
          {
            title: "Compare with the picture",
            text: 'Click "Show where" on a row. The area of the picture where I2Q read the value opens, with a dashed frame at the approximate place.',
          },
          {
            title: "Decide the line",
            text: 'A row read from a picture is marked "Read from a photo" in its Details. I2Q never accepts it on its own. Confirm, pick, correct or remove it as any other row.',
          },
        ],
      },
      {
        kind: "list",
        heading: "What you should know",
        items: [
          "Machine details from a nameplate (make, type, serial number, year) show on the case. They do not change the matching yet.",
          'I2Q also reads a picture in the mail body ("Picture in the mail body") and the files inside an attached mail, a sheet or a Word file ("In order.eml: photo.jpg"). It reads phone photos (HEIC) and old Word and Excel files.',
          "I2Q skips logos, signature images and banners. It does not send a picture it already read to the model again. It reads at most 6 pictures per mail.",
          'A picture with no item and no quantity says "I2Q read this picture: no part or quantity found".',
          'A row that one reading of a scan gave has the chip "Read once from the scan". Confirm it alone, never with the other rows.',
          "A picture that shows people gets no preview. Open the file instead.",
          'Customers often paste a whole spare parts list to show where an item sits. When the mail text names the items, the other rows of the list fold away under "From the picture, not asked". I2Q makes no line and no price of them.',
          'A reply can quote an earlier mail with its pictures. I2Q read them on the case of that mail and marks them "In the quoted mail: not read again".',
          'I2Q reads about 30 rows of a long list. When it read only the top rows of a list that gave lines, an open point starts with "Only the top". Compare the list with the picture and add the missing rows with "Add line".',
          'When the name of the item you pick shares no word with the line, "Pick item" says "The item name differs from the line". Compare the item with the picture. You can still pick it. An item that I2Q offers on the line gets no warning.',
        ],
      },
      {
        kind: "note",
        tone: "warning",
        title: "Never accepted on its own",
        text: "The model read this from the picture. I2Q accepts nothing read from a picture without your decision. The prepared quotation waits until you decide every line read from a picture.",
      },
      {
        kind: "link",
        text: "Open the explanation in Rules and countries",
        to: "/rules/glossary#reading-photos-and-scans",
      },
    ],
    labels: [
      "From the photo",
      "Attachments read",
      "Agreed (two readings)",
      "Read once",
      "Unclear",
      "Show where",
      "Used for",
      "Read from a photo",
      "Details",
      "Picture in the mail body",
      "I2Q read this picture: no part or quantity found",
      "Read once from the scan",
      "From the picture, not asked",
      "In the quoted mail: not read again",
      "Only the top",
      "Add line",
      "Pick item",
      "The item name differs from the line",
    ],
  },
  {
    id: "queue",
    title: "The queue: tabs, filters and your default view",
    summary:
      'The queue lists today\'s inquiries of your mailboxes, one row per case: received, customer, subject, lines found, the one action word, and who has it. The backlog chips above the tabs lead to older work; everything else sits behind "Filters".',
    keywords: [
      "queue",
      "filter",
      "tab",
      "search",
      "default",
      "mailbox",
      "area",
      "sender",
      "action",
      "today",
      "backlog",
    ],
    blocks: [
      {
        kind: "text",
        text: "The queue shows one row per case. Read the Action column first: it says in one word what the case needs from you.",
      },
      { kind: "figure", figure: "queue-row" },
      {
        kind: "terms",
        heading: "Your default view",
        terms: [
          {
            term: "To review",
            text: "Open inquiries with a proposal that still need a person. An inquiry that sales already replied to from the shared mailbox leaves this list and shows under Replied.",
          },
          {
            term: "Today",
            text: "Mail received today, of your mailboxes, yours or nobody's. Everything older is one click away under the backlog chips.",
          },
          {
            term: "Back to my default view",
            text: 'Inside "Filters": resets every filter to today. The filters are in the page address, so you can send a colleague the link to exactly what you see.',
          },
        ],
      },
      {
        kind: "terms",
        heading: "The six columns",
        terms: [
          { term: "Received", text: "Day and time." },
          {
            term: "Customer",
            text: 'The customer, and under it "to confirm" while the customer is a suggestion, "not identified" when I2Q found no account, or "Internal" for a Norican sender. Without an account the cell shows the mail domain of the sender. A public mail address such as gmail.com names no company, so the cell then shows the sender. The name of the sender shows when you rest on the cell. Managers also see the area.',
          },
          {
            term: "Subject",
            text: 'The link to the case. A thread with more mails carries a small "+2".',
          },
          {
            term: "Lines",
            text: '"3/4 found": how many rows have an item, and under it "1 to decide". Rest on the cell for the full sentence.',
          },
          {
            term: "Action",
            text: "One word: what the case needs from you next. It is the same word as the big button on the case. Your own open work is marked in colour.",
          },
          { term: "Who", text: 'The given name of the colleague who has the case, or "Nobody".' },
        ],
      },
      {
        kind: "terms",
        heading: "The action words",
        terms: [
          { term: "Reading", text: "I2Q has not finished with the mail. Usually a few minutes." },
          { term: "Could not read", text: "I2Q could not read this mail. Handle it by hand." },
          {
            term: "Nothing to do",
            text: "Closed, quoted already, the same inquiry as another case, or not an inquiry.",
          },
          {
            term: "Find customer",
            text: "Nobody was matched to the sender. Open the case and search by name or account number.",
          },
          {
            term: "Pick the customer",
            text: 'The suggested account is a guess, or two accounts fit. Open the case: the customer line offers "Confirm" with the name, "Not them" and the search; the big button says "Pick the customer".',
          },
          {
            term: "Confirm",
            text: "The customer is a suggestion. Open the case, read the mail and the evidence under Why, then confirm.",
          },
          { term: "Pick 2", text: "Two rows wait for a confirm or a pick in the draft." },
          { term: "Check", text: "A rule holds the case. The case shows the reason." },
          { term: "Prepare reply", text: "The rows are done. No draft yet." },
          { term: "Copy reply", text: "A reply is ready on the case." },
        ],
      },
      {
        kind: "terms",
        heading: "The backlog chips",
        terms: [
          {
            term: "open, all dates",
            text: "Every open inquiry of your scope, whatever date you picked. One click opens the list of exactly these. The number next to Queue in the menu shows the same count, after the new ones of your default view.",
          },
          {
            term: "open older than 7 days",
            text: "How many open inquiries are older than a week, whatever date you picked. One click opens them.",
          },
          {
            term: "Back for you",
            text: 'Cases that came back to you. See "Back for you".',
          },
        ],
      },
      {
        kind: "text",
        text: 'The menu shows two numbers next to Queue: "new" counts your default view, "open" counts every open inquiry of your scope over all dates. A chip changes only the days. Your mailboxes, areas and the other filters stay as they are, so a chip never shows you more than your own scope.',
      },
      {
        kind: "text",
        text: 'When the queue is empty, the chip "Received: today" names the date filter. "Show any date" shows the open mail of every date.',
      },
      {
        kind: "terms",
        heading: "The tabs",
        terms: [
          { term: "To review", text: "Your main list. Every inquiry with a proposal starts here." },
          {
            term: "Ready to quote",
            text: 'The cases that can be finished now: the customer is certain, every line is matched and priced, and no rule holds the case. New here? Start with this tab. The first time, a note over the tabs says so, with "Show them" and "Not now".',
          },
          {
            term: "Closed",
            text: "Done, not for us, or closed by a quotation sent from the shared mailbox. After an update of I2Q, a closed case can show a new reading of the mail. It stays closed, and your status, your customer and your line decisions stay.",
          },
          {
            term: "More",
            text: 'The other lists: "Needs a check", "Customer to confirm", "Replied", "Waiting for customer", "Not an inquiry", "Purchase orders (parked)", "Waiting for I2Q", "All except purchase orders" and "Portal / tender".',
          },
          {
            term: "Not an inquiry",
            text: "Mail that is neither an inquiry nor a purchase order: order status, invoices, delivery changes, complaints and other mail. Counted so we know the volume, not worked on.",
          },
          {
            term: "Purchase orders (parked)",
            text: "Purchase orders. Norican I2Q works on inquiries only: they are counted so we know the volume, not worked on. Handle them as today.",
          },
          {
            term: "Portal / tender",
            text: "Open notices from a purchasing portal or tender platform, and mails of customers who quote through a portal. Many are real inquiries: reply to them in the portal, not by mail.",
          },
        ],
      },
      {
        kind: "terms",
        heading: "Filters",
        terms: [
          {
            term: "Filters",
            text: 'One button on every width. The number on it counts the filters that differ from your default view. It opens a sheet with who has it: "Mine and unassigned", "Mine", "Unassigned" or "Everyone". Under it sit the mailboxes, the area, the sender, the type, the received dates and the four switches. At the bottom sits "Back to my default view".',
          },
          {
            term: "Search subject, customer, sender or item number",
            text: "Next to the Filters button. Searches the current tab only. Open All except purchase orders under More to search every case.",
          },
          {
            term: "Hide colleagues' mails",
            text: "Hides the inquiries a Norican colleague forwarded. Off by default, so you see them.",
          },
          {
            term: "Show replies as their own rows",
            text: "Shows every reply of a thread as its own row instead of folding it under the first mail. Off by default.",
          },
          {
            term: "Include mailboxes without a live item catalogue",
            text: "Also shows mailboxes whose items are not loaded yet. Off by default.",
          },
        ],
      },
      {
        kind: "text",
        text: "On a phone each row is a card: customer, subject, the action word and who has it, with the received time as a caption. The first time you open the queue, a note offers the 5-minute quick start once.",
      },
    ],
    labels: [
      "To review",
      "Closed",
      "More",
      "Today",
      "Back to my default view",
      "Received",
      "Customer",
      "Subject",
      "Lines",
      "Action",
      "Who",
      "to confirm",
      "not identified",
      "Internal",
      "Nobody",
      "Reading",
      "Could not read",
      "Nothing to do",
      "Find customer",
      "Pick the customer",
      "Confirm",
      "Check",
      "Prepare reply",
      "Copy reply",
      "open, all dates",
      "open older than 7 days",
      "Back for you",
      "Show any date",
      "Ready to quote",
      "Show them",
      "Not now",
      "Needs a check",
      "Customer to confirm",
      "Replied",
      "Waiting for customer",
      "Not an inquiry",
      "Purchase orders (parked)",
      "Waiting for I2Q",
      "All except purchase orders",
      "Portal / tender",
      "Filters",
      "Mine and unassigned",
      "Mine",
      "Unassigned",
      "Everyone",
      "Search subject, customer, sender or item number",
      "Hide colleagues' mails",
      "Show replies as their own rows",
      "Include mailboxes without a live item catalogue",
    ],
  },
  {
    id: "statuses",
    title: "What the statuses mean",
    summary:
      "Each case shows one status. Open cases tell you what is missing. Once someone works on a case, the work status takes over.",
    keywords: [
      "status",
      "glossary",
      "draft-ready",
      "needs-review",
      "duplicate",
      "done",
      "state",
      "deleted",
      "mailbox",
    ],
    blocks: [
      {
        kind: "terms",
        heading: "Set by I2Q when it reads the mail",
        terms: [
          {
            term: "Ready to quote",
            text: "The customer is certain and every line has an item, a quantity and a current price that passed the price checks. Check it, then use it.",
            system: "draft-ready",
          },
          {
            term: "Needs a check",
            text: "A row has no sure item yet, or all rows matched but a check is open (for example a price). The big button says Pick or Check: pick the item, say there is no item, or do the check.",
            system: "needs-review",
          },
          {
            term: "Customer to confirm",
            text: "The customer is only a suggestion.",
            system: "needs-review",
          },
          {
            term: "Customer not identified",
            text: 'I2Q found no customer. The customer line says "Find customer".',
            system: "needs-review",
          },
          {
            term: "Not processed",
            text: "I2Q could not read this mail. Handle it as today.",
            system: "needs-review",
          },
          {
            term: "Waiting for I2Q",
            text: "The mail waits for I2Q to read it.",
            system: "queued",
          },
          { term: "Processing", text: "I2Q reads the mail at this moment.", system: "processing" },
          {
            term: "Not an inquiry",
            text: "The mail is not an inquiry. I2Q counts it and prepares nothing.",
            system: "not-an-rfq",
          },
          {
            term: "Purchase order, not worked on",
            text: 'A purchase order. Norican I2Q works on inquiries only: it counts the mail and lists it under "Purchase orders (parked)". It prepares nothing.',
            system: "parked",
          },
          {
            term: "Copy of another mail",
            text: "The same mail is already a case. Work on the other case.",
            system: "duplicate",
          },
          {
            term: "In progress",
            text: "Someone has started to work on the case.",
            system: "in-progress",
          },
        ],
      },
      {
        kind: "terms",
        heading: "Work statuses (set by you or by I2Q)",
        terms: [
          {
            term: "Replied",
            text: "A reply without a quotation went out from the shared mailbox.",
            system: "replied",
          },
          {
            term: "Waiting for customer",
            text: "Set by sales: we asked the customer something.",
            system: "waiting_customer",
          },
          { term: "Done", text: 'Set by sales with "Mark done".', system: "done" },
          {
            term: "Not for us",
            text: "Set by sales: the mail is not for this team.",
            system: "not_for_us",
          },
          {
            term: "Closed",
            text: 'Closed by itself: a quotation or a reply went out from the shared mailbox, or the desk moved the mail. The caption on the case says which: a quotation or a reply sent, "Moved to the done folder of the shared mailbox" or "Deleted from the shared mailbox". The last two mean a person did that in Outlook. I2Q moved and deleted nothing. This rule is on.',
            system: "auto_closed",
          },
          {
            term: "No activity",
            text: 'Closed in I2Q only: nobody took the inquiry, set a status or confirmed a customer for 21 days, and I2Q found no reply. It leaves To review and the open counts. Nothing changes in Outlook or F&O. The Closed tab says "No activity for 21 days, closed in I2Q only". Reopen the case if it still needs a reply; it then has another 21 days. This rule is on.',
            system: "no_activity",
          },
        ],
      },
    ],
    labels: [
      "Ready to quote",
      "Needs a check",
      "Customer to confirm",
      "Customer not identified",
      "Find customer",
      "Not processed",
      "Waiting for I2Q",
      "Processing",
      "Not an inquiry",
      "Purchase order, not worked on",
      "Purchase orders (parked)",
      "Copy of another mail",
      "In progress",
      "Replied",
      "Waiting for customer",
      "Done",
      "Mark done",
      "Not for us",
      "Closed",
      "Moved to the done folder of the shared mailbox",
      "Deleted from the shared mailbox",
      "No activity",
    ],
  },
  {
    id: "learned",
    title: "Learned corrections",
    summary:
      "When you pick another item for a line, I2Q stores your correction. A correction that could apply to other customers too waits on this page until someone approves it.",
    keywords: ["learn", "correction", "alias", "approve", "retract", "teach", "disagree"],
    blocks: [
      {
        kind: "text",
        text: 'When the customer is certain or confirmed, I2Q stores your correction. You make a correction when you pick another item with "Pick item". The button "Undo" in the Details of the row takes the correction back too.',
      },
      {
        kind: "list",
        items: [
          "I2Q keeps a correction for the same customer and the same wording right away.",
          'A correction that could also change results for other customers goes to the page "Learned corrections". I2Q uses nothing on this page before someone approves it.',
          'The button "Approve" switches a correction on. The button "Retract" switches it off.',
        ],
      },
      {
        kind: "note",
        tone: "info",
        title: "Where corrections are used today",
        text: 'Today I2Q uses the stored corrections when you press "Try again" under More on a case it failed to read. When a new mail arrives, I2Q does not use them yet.',
      },
    ],
    labels: ["Pick item", "Undo", "Learned corrections", "Approve", "Retract", "Try again"],
  },
  {
    id: "dashboard",
    title: "The dashboard",
    summary:
      'The page "Dashboard" shows what I2Q did with the mail, how good it is, and whether the data is fresh. Managers see Summary, Operations and System; sales see My area and System; executives see Summary and the monthly report. Read only, and a test run: nothing is sent and nothing is written to F&O.',
    keywords: [
      "dashboard",
      "summary",
      "executive",
      "report",
      "metrics",
      "numbers",
      "kpi",
      "pilot",
      "freshness",
      "import",
    ],
    blocks: [
      {
        kind: "text",
        text: "Every figure counts completed weeks, Monday to Sunday. Summary compares the last 4 weeks with the 4 before. Charts show the current week as well, marked so far. A figure measured on fewer than 30 quotations, lines or cases shows as x of y with Too few to judge yet. It never shows as a percentage. Measured means counted from stored facts. Estimate means it contains an assumption.",
      },
      {
        kind: "terms",
        heading: "Summary (managers and executives)",
        terms: [
          {
            term: "Executives",
            text: "An executive sees the Summary and the monthly report for their areas, and Help. No queue, no case, no other dashboard tab and no cost or margin. IT gives the access in Entra: the role Executive plus the areas, usually all areas. A person who also works cases or leads a team needs the role Manager as well; the executive role then only adds.",
          },
          {
            term: "Matches what sales quoted",
            text: "The headline figure since 10 Oct 2026. It takes the item lines that sales quoted in F&O for these inquiries. It shows the share where I2Q proposed the same item in any mail of the thread. The single-mail figure on the same lines stands beside it. Revision mails, where a customer asks to change a quotation, show apart once the quotation dates are filled in. Until then they read Not measured yet. Only quotations found in F&O count.",
          },
          {
            term: "Right when I2Q is sure",
            text: "When I2Q marked an item, or named the customer, with certainty: how often sales quoted that item or that customer. The go-live bar is 99.5%.",
          },
          {
            term: "Inquiries handled",
            text: "Mails I2Q recognised as a request for a quotation and prepared, with the change against the period before.",
          },
          {
            term: "Done without a person",
            text: "The share of inquiry lines that I2Q matched with certainty. Also the share of inquiries with customer and items matched. Also the share ready to quote, with quantities and current prices checked, and how many of those matched the F&O quotation. The go-live bar is 75 % of lines.",
          },
          {
            term: "Ready within",
            text: "Time from the mail arriving to the prepared case, and the sales reply time where sent items are read.",
          },
          {
            term: "Quotation value",
            text: "The value of the F&O quotations behind the inquiries, quoted and won, per company currency. Never added up across currencies.",
          },
          {
            term: "Areas against the go-live bar",
            text: "Each area on its own, never added up: Meets bar, Below bar, Not enough data, or Import problem.",
          },
          {
            term: "Next steps waiting for a decision",
            text: "Open points such as a switch that is off or data that is still missing. Read only.",
          },
          {
            term: "Monthly report",
            text: "A two-page print of one month, for A4. Use Print or save as PDF in the report.",
          },
        ],
      },
      {
        kind: "terms",
        heading: "Operations (managers; Open in I2Q by age and Workload for admins)",
        terms: [
          {
            term: "Period",
            text: "1 week, 4 weeks or 13 weeks, always completed weeks. Every card of the tab follows it.",
          },
          {
            term: "Ready to quote",
            text: "The customer is certain and every line has an item, a quantity and a current price that passed the price checks. The counts open the queue with the same set.",
          },
          { term: "Needs a check", text: "At least one line needs a person." },
          {
            term: "Customer to confirm",
            text: "The customer needs to be confirmed before quoting.",
          },
          { term: "Where inquiries stand", text: "How the inquiries of the period split." },
          {
            term: "Inquiries per week",
            text: "Ready to quote and the rest that needs a person, per week.",
          },
          {
            term: "Quality by area",
            text: "Whole thread, single mail, right when sure, and how many inquiries had their quotation found in F&O, per area.",
          },
          {
            term: "Customer named by I2Q",
            text: "How often the customer was certain, suggested or not found, per area and per rule in plain words. Measured against the customer of the F&O quotation.",
          },
          {
            term: "What I2Q did each week",
            text: 'A chart of the inquiries per week, with the shares of a certain customer, every line matched and ready to quote. The figure "One click right" says how often a ready draft was the same as the F&O quotation. Show table opens the week by area table.',
          },
          {
            term: "Open in I2Q by age",
            text: "Per area, the open inquiries by age (under 1 day, 1 to 7, 7 to 14, over 14 days). In shadow mode sales people reply from Outlook and F&O, so open means nobody closed the case in I2Q.",
          },
          {
            term: "Workload",
            text: "The open cases each person holds, shown once anyone holds a case. The dashboard shows nothing else per person.",
          },
          {
            term: "Pilot",
            text: "What the pilot sales people decided and the feedback they gave. It shows Not started before any activity.",
          },
          {
            term: "Team",
            text: "For admins, in the menu: opens Operations at Open in I2Q by age. Open in I2Q by age and Workload are for admins only.",
          },
        ],
      },
      {
        kind: "terms",
        heading: "System (everyone) and My area (sales)",
        terms: [
          { term: "Mail import", text: "When mail was last read. It runs every 5 minutes." },
          {
            term: "Mail mix",
            text: 'How the mail splits: inquiries, not an inquiry, "Purchase orders, not worked on" (counted only) and waiting for I2Q.',
          },
          {
            term: "Master data",
            text: "How fresh the copy of customers, items, prices and quotations from F&O is, against its limit.",
          },
          {
            term: "Switches",
            text: "What I2Q does on its own, and whether each switch is on. The switches: prepare replies. Close inquiries nobody worked on for 21 days (in I2Q only, on). Close inquiries replied to from the shared mailbox (on). File inquiries with an F&O quotation. Count exact-number lines as decided (UK). Show who usually handles a customer. Read only; Timo decides each one.",
          },
          {
            term: "Margin thresholds",
            text: "For people with margin rights: the threshold per area.",
          },
          {
            term: "My area",
            text: "For sales: last week in your areas, how often I2Q matched what sales quoted, and Open my queue.",
          },
        ],
      },
      {
        kind: "text",
        text: 'What the model calls cost, how the mail import runs and how the models reply: see the page "Cost and runs". It is for admins.',
      },
      { kind: "link", text: "Read how I2Q counts the cost", to: "/help#cost-and-runs" },
    ],
    labels: [
      "Dashboard",
      "Cost and runs",
      "Summary",
      "Operations",
      "System",
      "My area",
      "Matches what sales quoted",
      "Right when I2Q is sure",
      "Inquiries handled",
      "Done without a person",
      "Ready within",
      "Quotation value",
      "Areas against the go-live bar",
      "Next steps waiting for a decision",
      "Monthly report",
      "Print or save as PDF",
      "Ready to quote",
      "Needs a check",
      "Customer to confirm",
      "Where inquiries stand",
      "Inquiries per week",
      "Quality by area",
      "Customer named by I2Q",
      "What I2Q did each week",
      "One click right",
      "Show table",
      "Open in I2Q by age",
      "Workload",
      "Pilot",
      "Team",
      "Mail import",
      "Mail mix",
      "Purchase orders, not worked on",
      "Master data",
      "Switches",
      "Margin thresholds",
      "Open my queue",
      "Measured",
      "Estimate",
      "Too few to judge yet",
      "Not measured yet",
      "Not started",
      "Revision mails",
    ],
  },
  {
    id: "not-yet",
    title: "What is not there yet",
    summary:
      "I2Q does not send mail and does not create quotations in F&O. It does not know stock, and it knows a lead time only where a desk quoted the item before. These come later, step by step.",
    keywords: ["missing", "roadmap", "send", "write", "f&o", "stock", "limits"],
    blocks: [
      {
        kind: "list",
        items: [
          "Sending: I2Q creates no mail and no Outlook draft. You copy the reply text yourself.",
          'Writing to F&O: "Create quotation in the order system" is switched off. You create the quotation in F&O as today.',
          "Stock: I2Q does not know it.",
          'Lead time: I2Q shows it with a clock when F&O holds one for the item. A clock with an arrow means a desk quoted the item before (Denmark and India on many lines). The Lead cell then names the quotation and its date. That is context, not a lead time to promise. Otherwise the Lead cell says "No lead time in F&O" with a dashed circle, or "Not looked up yet" with an hourglass. The purchase lead times of F&O are not loaded yet. The lead time of the item master is built and switched off. When IT switches it on for an area, the Lead cell names it, for example "Item master: sales lead time". The reply then says "about 21 days, to be confirmed".',
          "A discount you give by hand: I2Q does not know it. It shows the list price.",
          "Handwritten changes on a scanned page: I2Q does not read them.",
        ],
      },
      {
        kind: "text",
        text: "IT switches each of these on only after we measured it in shadow mode and agreed it with the business.",
      },
      {
        kind: "text",
        text: "Roadmap shows what is live, what runs in shadow mode, what is built but switched off, and what is planned. Where a discussion exists, it links to it.",
      },
      { kind: "link", text: "See the roadmap", to: "/roadmap" },
    ],
    labels: ["Create quotation in the order system", "Roadmap"],
  },
  {
    id: "rules",
    title: "Entity rules: why a case is held or flagged",
    summary:
      "Each company has its own rules for inquiries: who may not get a quotation, which lines need a person, and which prices need a check. The case page shows every reason with what to do next.",
    keywords: [
      "rules",
      "entity rules",
      "held",
      "blocked",
      "flagged",
      "warning",
      "competitor",
      "sanctioned",
      "portal",
      "signal code",
      "successor",
      "freight",
      "margin",
      "hsn",
      "sales pool",
      "goa",
      "master data checks",
      "item master",
    ],
    blocks: [
      {
        kind: "text",
        text: 'The rules come from the Go Autonomous set-up of each company, written down so you can see them. When a rule holds a case, the big button says "Check" with the reason. It opens the "Details" sheet at the checks, the panel "Why this case needs attention". Each reason says what is wrong and, under it, what to do next. A reason about one row also shows in the Details of that row. In the queue, the Action column says "Check". Next to a reason, "Why?" explains the rule and links to the guide of your country.',
      },
      { kind: "link", text: "Open rules and countries", to: "/rules" },
      {
        kind: "terms",
        heading: "The three modes of a rule",
        terms: [
          {
            term: "Block",
            text: 'The case is held. The reason is marked "Holds the case". The quotation is not ready before you deal with the reason.',
            system: "block",
          },
          {
            term: "Warn",
            text: "The reason is listed on the case and in the queue. Nothing is held.",
            system: "warn",
          },
          {
            term: "Off",
            text: "The rule does not run for that company.",
            system: "off",
          },
        ],
      },
      {
        kind: "text",
        text: 'Some reasons are only a note, for example a header field that is "Not available yet" in the master data. Notes sit under "Other notes" in the checks and never show in the queue. A notification or a newsletter shows no checks.',
      },
      {
        kind: "terms",
        heading: "The rules",
        terms: [
          {
            term: "Mail from a competitor goes to manual handling",
            text: "The sender, or the original sender of a forwarded mail, is on the competitor list. Some customers are also competitors; for them it is only a warning.",
            system: "gate.competitor",
          },
          {
            term: "Mail from a sanctioned country goes to manual handling",
            text: "The sender's address belongs to a sanctioned country.",
            system: "gate.sanctioned",
          },
          {
            term: "Customers who quote through a portal are handled by hand",
            text: "The customer or the sender is on the portal list of the company.",
            system: "gate.portal",
          },
          {
            term: "Some UK customers are quoted from Wheelabrator Germany",
            text: "The customer is on the list of UK accounts that are quoted from Germany.",
            system: "gate.uk_wgg",
          },
          {
            term: "UK export customers are flagged",
            text: "The customer is in the UK export customer group.",
            system: "gate.uk_export",
          },
          {
            term: "Lines in a Media item group need carriage set by hand",
            text: "Media lines get no automatic carriage line.",
            system: "gate.media",
          },
          {
            // ste-allow: backend-seed
            term: "Parts for a service visit are handled by hand",
            text: "A line is in a service item group.",
            system: "gate.service_visit",
          },
          {
            // ste-allow: backend-seed
            term: "Lines in an item group outside parts sales are flagged",
            text: "For example UK Impact or DK OEM lines.",
            system: "gate.out_of_scope_group",
          },
          {
            term: "Quotations above the approval limit need a manager",
            text: "The draft total is above the approval limit in its currency.",
            system: "gate.doa_limit",
          },
          {
            term: "Lines without a valid HSN code are flagged",
            text: "India only: the HSN code is missing, the placeholder 6666, or not 4, 6 or 8 digits. A 4-digit code such as 8424 is valid. A note to check: it never holds the quotation. I2Q never guesses the code.",
            system: "gate.hsn",
          },
          {
            term: "Items whose signal code needs a check are flagged",
            text: "For example a new item, a revision or an item that does not move.",
            system: "gate.signal_review",
          },
          {
            term: "Generic items need the machine variant",
            text: "Ask the customer which variant is meant. A warning, because sales often quote the generic item as it is.",
            system: "gate.signal_generic",
          },
          {
            term: "Items with a successor show the successor",
            text: "The line names the item it replaces.",
            system: "gate.successor",
          },
          {
            term: "A rule check that fails holds the case",
            text: "If a check cannot run, the case is held instead of passing unchecked.",
            system: "gate.evaluation_failed",
          },
          {
            term: "Sales pool and CRM fields come from the item group with most lines",
            text: "Two groups with the same number of lines, or a group without a pool, give a reason to pick the pool.",
            system: "header.pool",
          },
          {
            term: "Header fields that are not in master data yet are listed",
            text: "Sales unit, payment terms and delivery terms: take them from the customer in F&O.",
            system: "header.not_available",
          },
          {
            term: "Header fields come from the customer's last quotation where master data has none",
            text: "Only quotations made before the inquiry arrived count. So I2Q never uses the quotation made for this inquiry. A note names the quotation and its date. I2Q shows the sales pool and the sales taker of that quotation, but never uses them.",
            system: "header.from_last_quotation",
          },
          {
            term: "Freight is added by the entity's freight model",
            text: "By order value bands, in the item price, or not on the quotation, depending on the company.",
            system: "pricing.freight",
          },
          {
            term: "Lines below the contribution threshold need a manager",
            text: "A manager approves the price. Only managers see the figures.",
            system: "pricing.margin",
          },
          {
            term: "A line without a valid price is never quoted at zero",
            text: "Ask for a price.",
            system: "pricing.no_price",
          },
          {
            term: "Lead times above 99 days are flagged",
            text: "Check the lead time before quoting.",
            system: "pricing.lead_time",
          },
          {
            term: "A ship date after the requested date is flagged",
            text: "Today plus the lead time is later than the date the customer asked for.",
            system: "pricing.after_requested",
          },
          {
            term: "A price without a lead time is flagged",
            text: "The ship date would be today, which often means an expired trade agreement. Switched off until lead times are in the master data.",
            system: "pricing.ship_today",
          },
          {
            term: "Prices older than 18 months are flagged for re-costing",
            text: "The price has not changed for more than 18 months.",
            system: "pricing.price_age",
          },
          {
            term: "A price far from the customer's last quoted price is noted",
            text: "More than 15 % above or below the last quotation of the same item in the same currency, made before the inquiry arrived. A note only: nothing is held or changed.",
            system: "pricing.vs_last_quote",
          },
          {
            term: "An item that is not in the item master is flagged",
            text: "The item number of the line is not in the item master of your company. Check the item number.",
            system: "preflight.item_exists",
          },
          {
            term: "An item that is not set up for the legal entity is flagged",
            text: "The item exists, but F&O has not released it for your company. Ask the master data team.",
            system: "preflight.item_released",
          },
          {
            term: "An item that is blocked for sales is flagged",
            text: "F&O blocks the item for sales. Check the item status or quote the replacement.",
            system: "preflight.item_blocked",
          },
          {
            term: "A line without a valid list price is flagged before the quotation",
            text: "The line has no list price that is valid on the day the mail arrived. Ask for a price.",
            system: "preflight.price_available",
          },
          {
            term: "A quantity that is not above zero is flagged",
            text: "Check the quantity in the mail and correct the line.",
            system: "preflight.quantity",
          },
          {
            term: "A customer account that is marked do not use is flagged",
            text: "The customer master marks the account as do not use. Pick the account that replaces it.",
            system: "preflight.customer_valid",
          },
          {
            term: "A currency that is not the customer's sales currency is noted",
            text: "The mail names another currency than the sales currency of the account. A note only.",
            system: "preflight.currency",
          },
          {
            term: "An inquiry reference that was quoted before is flagged",
            text: "An earlier quotation of this customer has the same inquiry reference. Check if this mail is a follow-up.",
            system: "preflight.duplicate_reference",
          },
        ],
      },
      {
        kind: "note",
        tone: "info",
        title: "Master data checks",
        text: 'The last eight rules are the master data checks. I2Q runs them on the finished draft, against the customer and item data of F&O. Their reasons stand under "Checks" in the "Details" sheet, like every other reason. They are warnings and notes: they hold nothing. IT turns them on for one company at a time. Until then you see no such reason. The tab "Live settings" lists them under "Master data checks".',
      },
      {
        kind: "note",
        tone: "info",
        title: "Who changes the rules",
        text: 'Rules are changed by IT on request. This page shows the active set. Managers and admins see every rule of their companies with its mode and source in the tab "Live settings" of "Rules and countries". Sales people see the reasons on their cases and every rule explained on the same page.',
      },
    ],
    labels: [
      "Check",
      "Details",
      "Why this case needs attention",
      "Holds the case",
      "Other notes",
      "Not available yet",
      "Live settings",
      "Rules and countries",
      "Rules are changed by IT on request. This page shows the active set.",
      "Checks",
      "Master data checks",
    ],
  },
  {
    id: "rules-and-countries",
    title: "Rules and countries: every rule explained",
    summary:
      'The page "Rules and countries" explains every rule and every country speciality of Norican I2Q: what it checks, why, what you see and what to do. It is read only.',
    keywords: [
      "rules",
      "countries",
      "country guide",
      "glossary",
      "why",
      "warning",
      "blocked",
      "speciality",
      "denmark",
      "united kingdom",
      "north america",
      "india",
      "france",
      "germany",
      "spain",
      "mexico",
      "czechia",
    ],
    blocks: [
      {
        kind: "text",
        text: 'Open "Rules and countries" in the menu under Learn and help. It opens the guide of your country. Each speciality says whether it is active or planned, and whether you see it as blocked, as a warning or as info.',
      },
      {
        kind: "list",
        heading: "The tabs",
        items: [
          "Countries: one guide per country, with how inquiries arrive and its specialities.",
          "All rules: the rules for every country, with a search and filters.",
          "How I2Q works: the six steps from a mail to a draft, and what warning or blocked means.",
          "Live settings: the mode of every rule per company. Only managers and admins see this tab.",
        ],
      },
      {
        kind: "text",
        text: 'On a case, "Why?" next to a reason in the checks of the Details sheet opens the matching explanation. From there you open the full explanation or the guide of your country.',
      },
      { kind: "link", text: "Open rules and countries", to: "/rules" },
    ],
    labels: ["Rules and countries", "Why?", "Live settings"],
  },
  {
    // The usage log (13 Oct 2026). The facts are those of the code: the vocabulary of
    // backend/src/rfq_foundry/usage_vocabulary.py (what can be stored), the 180 days and
    // the group of 3 of backend/src/rfq_foundry/store/usage.py, the roles of auth/roles.py.
    id: "usage-log",
    title: "What I2Q records about your use",
    summary:
      "I2Q records how people use it: the pages, the buttons and the errors. It records no mail text and no search words. The record helps to improve the product.",
    keywords: [
      "usage",
      "usage log",
      "recorded",
      "tracking",
      "privacy",
      "personal data",
      "sign-in",
      "who sees",
      "how long",
      "simulation",
    ],
    blocks: [
      {
        kind: "text",
        text: "The usage log shows which pages and buttons people use, and where they stop. The build team uses it to improve the product. It is not a record of your decisions on a case: that record is the history of the case.",
      },
      {
        kind: "list",
        heading: "What I2Q records",
        items: [
          "Your sign-ins, with the day and the time.",
          "The pages you open, and how long you are active on each page.",
          "The buttons, menus and dialogs you use. I2Q records that you used them, not what you typed.",
          "An error that you see, for example a page that did not load.",
          "How long a page takes to load.",
          "The kind of device: a computer, a phone or the Outlook pane.",
          "For a search: the number of characters, and whether it looks like a number.",
        ],
      },
      {
        kind: "list",
        heading: "What I2Q never records",
        items: [
          "The text of a mail, or its subject.",
          "The name or the account of a customer.",
          "The words you search for.",
          "What you type in a dialog, for example a price or a feedback text.",
          "A password, a token or your network address.",
          "A practice case of the training.",
        ],
      },
      {
        kind: "list",
        heading: "How long I2Q keeps it, and who sees it",
        items: [
          "I2Q keeps a usage record for 180 days. Then I2Q removes it.",
          'Only an admin sees the page "Usage": the totals and the use of each person.',
          "Managers, sales people and executives do not see the page.",
          "The figures leave out the simulation personas of a test, until an admin adds them.",
        ],
      },
      {
        kind: "note",
        tone: "info",
        title: "It stays in I2Q",
        text: "The usage log stays in Norican I2Q. I2Q sends it to no other company and uses no outside tracking service.",
      },
    ],
    labels: ["Usage"],
  },
  {
    // Cost and runs (14 Oct 2026). The facts are those of the code: the meter of
    // backend/src/rfq_foundry/finops/meter.py (what is recorded), the caps of
    // finops/guards.py, the prices of finops/prices.yaml, the roles of auth/roles.py.
    id: "cost-and-runs",
    title: "Cost and runs",
    summary:
      "What the model calls cost, how the mail import runs and how the models reply. For admins.",
    keywords: [
      "cost",
      "finops",
      "budget",
      "cap",
      "spend",
      "tokens",
      "invoice",
      "azure",
      "runs",
      "model health",
      "alerts",
      "operations",
    ],
    blocks: [
      {
        kind: "text",
        text: 'The page "Cost and runs" shows what the model calls of I2Q cost, how the mail import runs and how the models reply. Open it in the menu under Insight.',
      },
      {
        kind: "terms",
        heading: "The four tabs",
        terms: [
          {
            term: "Cost",
            text: "The cost of today, this week and this month, and the forecast for the month. Also the caps, the cost by model, step, reason and entity, the most expensive cases and the last 30 days.",
          },
          {
            term: "Runs",
            text: "The mail import runs, the re-read of stored mail with its size and estimated cost, the data jobs and the errors of each step.",
          },
          {
            term: "Model health",
            text: 'Calls, errors, "Too many requests", the content filter and the time of each model and step.',
          },
          {
            term: "Alerts",
            text: "The caps and alert levels that were reached, and the Azure alerts when that view is connected.",
          },
        ],
      },
      {
        kind: "list",
        heading: "Where the figures come from",
        items: [
          "I2Q records each model call with its tokens, its time and its result.",
          "I2Q never records the text of the mail, the prompt or the reply of the model.",
          "The cost is the tokens times the list price of each model. The figures are estimates.",
          'I2Q records the calls from the day of the update. Before that day, the month has no recorded figures. "This month" then shows "Recorded since" and that day.',
          'The forecast is the month to date plus the mean of the last 7 recorded days for each day left. Before one full recorded day, it shows "Not yet".',
          'The Azure invoice is the authority. With the Azure view, "Model accounts on Azure" shows the whole month of the model accounts. These accounts also serve other users, not only I2Q.',
          "The card Azure shows the difference to the recorded cost only when I2Q recorded the whole month.",
          'Without the Azure view, the page says "The Azure view is not connected." The figures then come from the calls that I2Q records.',
          '"Median time" in "Model health" includes the waits before a new try.',
        ],
      },
      {
        kind: "list",
        heading: "The caps",
        items: [
          "A cap is a limit in EUR for one kind of work, for a day or a month.",
          'When a cap is reached, its state is "Paused". The work waits for the next day or the next month.',
          "The re-read of stored mail waits. The cases stay as they are.",
          "A case that a person sent through I2Q again does not wait. For example, a sales person confirms a customer, and I2Q reads the other open cases of that sender again.",
          'In a re-read or a replay, a picture or a scan is not read. The case shows "Not read: the daily limit for reading pictures is reached".',
          "New mail never stops. Its level only gives an alert. I2Q always reads the pictures and scans of new mail and of a case that a person sent again.",
          'An alert level gives "Above the alert level". Nothing stops.',
        ],
      },
      {
        kind: "list",
        heading: "Who sees what",
        items: [
          "Admins see the four tabs.",
          'The entities cover every entity. The cases cover your areas. A case of another area shows as "Another area".',
          'Managers, sales people and executives do not see the page. "What runs today" shows them "Model cost this month".',
        ],
      },
    ],
    labels: [
      "Cost and runs",
      "Cost",
      "Runs",
      "Model health",
      "Alerts",
      "Too many requests",
      "The Azure view is not connected.",
      "Paused",
      "Not read: the daily limit for reading pictures is reached",
      "Above the alert level",
      "Another area",
      "Model cost this month",
      "This month",
      "Recorded since",
      "Not yet",
      "Model accounts on Azure",
      "Median time",
    ],
  },
  {
    // Master data (15 Oct 2026). The facts are those of the code: the kinds and the fix
    // texts of backend/src/rfq_foundry/master_data/kinds.py, the nightly stage of
    // mail/ingest.py, the capabilities of auth/roles.py. Labels from
    // features/master-data/labels.ts.
    id: "master-data",
    title: "Master data",
    summary:
      "The records of F&O that hurt I2Q cases in the last 30 days, and how to fix each one. For everyone who works on cases.",
    keywords: [
      "master data",
      "defect",
      "data owner",
      "data quality",
      "cleanup",
      "account",
      "item",
      "price",
      "lead time",
      "hsn",
      "blocked",
      "csv",
      "export",
    ],
    blocks: [
      {
        kind: "text",
        text: 'The page "Master data" lists the records of F&O that hurt I2Q cases in the last 30 days. Open it in the menu under Insight. The records with the most cases come first.',
      },
      {
        kind: "terms",
        heading: "The columns",
        terms: [
          {
            term: "Kind",
            text: 'What is wrong with the record, for example "No lead time". The cards above the table count the records of each kind.',
          },
          { term: "Area", text: "The company in F&O that holds the record." },
          { term: "Record", text: "The account number or the item number, with its name." },
          { term: "What is wrong", text: "One sentence about the defect." },
          {
            term: "Cases in 30 days",
            text: 'The cases that the record hurt. Click a case to open it. A case of another area shows as "Another area".',
          },
          { term: "First seen", text: "The first night that I2Q found the defect." },
          { term: "Status", text: '"Open", "In progress", "Not a defect" or "Fixed in F&O".' },
          { term: "How to fix in F&O", text: "Where to go in F&O and which field to fill in." },
        ],
      },
      {
        kind: "terms",
        heading: "The kinds",
        terms: [
          { term: "HSN code missing", text: "An item of India has no valid HSN code." },
          {
            term: "No price",
            text: "A quoted item has neither a price for the customer nor a list price.",
          },
          {
            term: "No lead time",
            text: "F&O has no lead time for the item, and no quotation or offer named one.",
          },
          {
            term: "Twin accounts",
            text: "Two accounts of one company have the same name and postcode. The row shows the lowest account number.",
          },
          {
            term: "Account inactive in F&O",
            text: "The account still sends inquiries, but it is inactive in F&O.",
          },
          {
            term: "Account marked do not use",
            text: "The name or the street of the account says do not use, but it names no live successor account.",
          },
          {
            term: "Item blocked in this area",
            text: "The item is blocked in this company. It can be sellable in another one.",
          },
          {
            term: "Address without postcode or country",
            text: "The primary address of the account lacks the postcode or the country.",
          },
          {
            term: "Item not released in this area",
            text: "Customers ask for the item, but only another company has it.",
          },
          {
            term: "Item name missing",
            text: "The item has no name, or its name is only the item number.",
          },
        ],
      },
      {
        kind: "steps",
        steps: [
          {
            title: "Find the records",
            text: 'Click a card of a kind, or set "Kind" and "Area" under "Filters".',
          },
          {
            title: "Check a case",
            text: "Open one of the cases in the row to see what went wrong.",
          },
          {
            title: "Fix the record in F&O",
            text: 'Do what "How to fix in F&O" says, or give the row to the data owner.',
          },
          {
            title: "Mark the row",
            text: 'Click "Mark in progress" while the fix waits. Click "Mark as open" to undo the mark.',
          },
          {
            title: "Or keep the record",
            text: 'If the record is right, click "Mark as not a defect". Write the "Reason" in one sentence and click "Save".',
          },
          {
            title: "Wait one night",
            text: 'I2Q checks every record again each night. A record that is correct now shows "Fixed in F&O".',
          },
        ],
      },
      {
        kind: "list",
        heading: "Good to know",
        items: [
          '"Checked" shows the time since when I2Q checked every kind. Before that, the page says "The first check runs tonight."',
          "A record that is not in the copy of F&O any more keeps its row. I2Q cannot say that it is fixed.",
          "The cards count each case once, also when it hurt more than one record of a kind.",
          'The list shows the rows "Open" and "In progress". Tick "Show records without a case in 30 days" to also see the records without a recent case.',
          '"Export CSV" gives the list as a file for the data owner. The file holds the records only, without case numbers or persons.',
          'Set "Status" to "Fixed in F&O" to see the records of the last 90 days that a person fixed.',
          'With no defect in your areas, the page says "No open defects in your areas."',
        ],
      },
      {
        kind: "list",
        heading: "Who sees what",
        items: [
          "Everyone who works on cases sees the records of their areas, and can mark them. This includes sales people.",
          "An admin sees the records of every area.",
          "Executives do not see the page.",
          "A mark keeps the role and the date, not your name.",
        ],
      },
      {
        kind: "note",
        tone: "info",
        title: "I2Q only reads",
        text: "I2Q writes nothing to F&O, CRM or a mailbox. A person fixes each record in F&O. The check makes no model call.",
      },
    ],
    labels: [
      "Master data",
      "Kind",
      "Area",
      "Record",
      "What is wrong",
      "Cases in 30 days",
      "First seen",
      "Status",
      "How to fix in F&O",
      "Open",
      "In progress",
      "Not a defect",
      "Fixed in F&O",
      "Another area",
      "HSN code missing",
      "No price",
      "No lead time",
      "Twin accounts",
      "Account inactive in F&O",
      "Account marked do not use",
      "Item blocked in this area",
      "Address without postcode or country",
      "Item not released in this area",
      "Item name missing",
      "Filters",
      "Mark in progress",
      "Mark as open",
      "Mark as not a defect",
      "Reason",
      "Save",
      "Checked",
      "The first check runs tonight.",
      "Show records without a case in 30 days",
      "Export CSV",
      "No open defects in your areas.",
    ],
  },
  {
    // What runs today (15 Oct 2026). The facts are those of the code: the builder
    // backend/src/rfq_foundry/platform_status.py, which reuses the cost summary, the job
    // rows, the switch states and the data health. Labels from features/platform/labels.ts.
    id: "what-runs-today",
    title: "What runs today",
    summary:
      "The live facts of I2Q on one page: areas, shadow mode, steps on their own, jobs, cases, cost and version. For everyone.",
    keywords: [
      "status",
      "platform",
      "what runs",
      "today",
      "shadow mode",
      "switches",
      "jobs",
      "version",
      "deploy",
      "budget",
      "mailboxes",
      "go-live",
    ],
    blocks: [
      {
        kind: "text",
        text: 'The page "What runs today" shows the facts of I2Q as they are now. Nobody types them in. Open it in the menu under Insight. It replaces the status slide.',
      },
      {
        kind: "terms",
        heading: "The cards",
        terms: [
          {
            term: "Areas and mailboxes",
            text: 'Each of your areas, "Live" or "Not live yet", its shared intake mailboxes and its cases in 30 days.',
          },
          {
            term: "Shadow mode",
            text: '"Nothing is sent. I2Q writes nothing to F&O." The card also shows "Gates to F&O closed" and "Outgoing connections limited".',
          },
          {
            term: "What I2Q does on its own",
            text: 'Each step that I2Q can do without a person, "On" or "Off". "Waits for a business decision" links to that decision.',
          },
          {
            term: "Jobs",
            text: 'The mail import and the data jobs, each with its "Last good run".',
          },
          { term: "Cases per day", text: "The new cases of the last 14 days, by area." },
          {
            term: "Done without a person",
            text: "The share of cases that needed no person, against the go-live bar. The Dashboard shows the same figure.",
          },
          {
            term: "Model cost this month",
            text: 'The cost of the model calls this month and the "Forecast", against the "Month alert level". "Not measured" means that I2Q could not read the cost.',
          },
          {
            term: "I2Q version",
            text: '"Build", "Rule set" and "Running since": the time I2Q last started. Each new version starts it again.',
          },
          { term: "Data freshness", text: "Whether the copies of F&O and CRM are up to date." },
          {
            term: "Business decisions",
            text: 'The number of "Open decisions", with a link to the page.',
          },
        ],
      },
      {
        kind: "list",
        heading: "Where the figures come from",
        items: [
          'Each figure comes from the page that owns it. For the details, open "Cost and runs", the "Dashboard" or the "Roadmap".',
          "The figures can be up to 5 minutes old. Open the page again for new figures.",
          "The page names a shared mailbox, never a person.",
        ],
      },
      {
        kind: "list",
        heading: "Who sees what",
        items: [
          "Everyone sees the page, sales people and executives too.",
          "The areas, the mailboxes, the cases and the share without a person are those of your areas.",
          "Shadow mode, the switches, the jobs, the cost and the version are the same for everyone.",
        ],
      },
    ],
    labels: [
      "What runs today",
      "Areas and mailboxes",
      "Live",
      "Not live yet",
      "Shadow mode",
      "Nothing is sent. I2Q writes nothing to F&O.",
      "Gates to F&O closed",
      "Outgoing connections limited",
      "What I2Q does on its own",
      "On",
      "Off",
      "Waits for a business decision",
      "Jobs",
      "Last good run",
      "Cases per day",
      "Done without a person",
      "Model cost this month",
      "Forecast",
      "Month alert level",
      "I2Q version",
      "Build",
      "Rule set",
      "Running since",
      "Data freshness",
      "Business decisions",
      "Open decisions",
      "Cost and runs",
      "Dashboard",
      "Roadmap",
      "Not measured",
    ],
  },
  {
    // Business decisions (15 Oct 2026). A business decision is a topic of the register
    // (store/discussions.py) with a frame: owner role, milestone, options, today's default.
    // The eleven seeds are backend/alembic/seeds/business_decisions_2026-10-15.json.
    // Labels from features/discussions/labels.ts.
    id: "business-decisions",
    title: "Business decisions",
    summary:
      "The decisions the business takes before the go-live, with the options and what I2Q does today. Everyone reads them. Managers record the decision.",
    keywords: [
      "decision",
      "business",
      "go-live",
      "rollout",
      "milestone",
      "owner",
      "options",
      "steering",
      "criteria",
      "key users",
    ],
    blocks: [
      {
        kind: "text",
        text: 'The page "Business decisions" lists the decisions that the business takes. It replaces the decision slides. The decisions are in the order of the milestone that needs them, the open ones first. Open it in the menu under Plans and decisions.',
      },
      {
        kind: "terms",
        heading: "What each decision shows",
        terms: [
          {
            term: "Owner",
            text: "The role that takes the decision. I2Q names roles, not persons.",
          },
          { term: "Needed for", text: "The milestone of the project plan, with its date." },
          {
            term: "Options",
            text: "Each option with its properties: what it means, the work it needs and what changes for sales people. No option is marked as the right one.",
          },
          { term: "What I2Q does today", text: "What I2Q runs with until the business decides." },
          {
            term: "Decided by",
            text: 'The role that decided, the date and the "Option picked".',
          },
        ],
      },
      {
        kind: "list",
        heading: "The milestones",
        items: [
          "Backlog agreed: 16 Oct 2026.",
          "Critical data validated: 23 Oct 2026.",
          "Key users recommend: 30 Oct 2026.",
          "Business presentation: 6 Nov 2026.",
          "Go or no-go: 9 Nov 2026.",
        ],
      },
      {
        kind: "steps",
        steps: [
          {
            title: "Read a decision",
            text: "Click the title of the decision to open it with its comments.",
          },
          {
            title: "Give your view",
            text: "Write a comment under the decision. Everyone who signs in can comment.",
          },
          {
            title: "Change the frame",
            text: 'Managers only: click "Edit owner, date and options", change the values and click "Save". For a decided one, set the status back to open first.',
          },
          {
            title: "Record the decision",
            text: 'Managers only: set the status "Decided". Fill in the "Role that decided" and the "Option picked", then save.',
          },
        ],
      },
      {
        kind: "note",
        tone: "info",
        title: "Options, not verdicts",
        text: "I2Q shows the options and their properties. The business decides. A decision changes in the app at once, without a new version of I2Q.",
      },
    ],
    labels: [
      "Business decisions",
      "Owner",
      "Needed for",
      "Options",
      "What I2Q does today",
      "Decided by",
      "Option picked",
      "Edit owner, date and options",
      "Save",
      "Decided",
      "Role that decided",
    ],
  },
  {
    // Process mining (15 Oct 2026). How sales people quote today, before I2Q. The facts are
    // those of the contract of the page: the event log of backend/src/rfq_foundry/
    // process_mining/, the pseudonyms and the minimum group of 5 (process_mining/privacy.py),
    // the capability see_process_mining of auth/roles.py. Labels from
    // features/process-mining/labels.ts.
    id: "process-mining",
    title: "Process mining",
    summary:
      "How sales people quote today, before I2Q: the steps, the time between them, the tools and where I2Q helps. For the holder of the process mining role.",
    keywords: [
      "process mining",
      "event log",
      "value stream",
      "process map",
      "variant",
      "rework",
      "baseline",
      "touchless",
      "lead time",
      "conversion",
      "tools",
      "excel",
      "defender",
      "go autonomous",
      "report",
      "download report",
      "word",
      "powerpoint",
      "core model",
      "golden process",
      "cmp",
      "clicklearn",
    ],
    blocks: [
      {
        kind: "text",
        text: 'The page "Process mining" shows how a quotation is made today, before I2Q. It reads the mails of the shared mailboxes, the quotations and orders of F&O, the tools from Defender and the F&O forms from NOVA. Open it in the menu under Insight.',
      },
      {
        kind: "text",
        text: 'Each tile is one pain point: work that costs time or orders. The number is cases a month, at least. In "Where it hurts", the colour and the word of a cell show how bad the area is. Dark blue is High, blue is Medium, light blue is Low, grey is For information. The report files use the same colours. Each row names its unit, and "Counted a month (High and Medium)" adds its High and Medium cells. The row "Pain points counted on quotations (High and Medium)" adds the High and Medium cells of the quotation rows, and shows 0 for an area with none. "Different ways" is in the group "For information" and is not added. "Measured" is counted, "Strong sign" is compared with a normal working day, and "Estimate" uses an assumed time.',
      },
      {
        kind: "terms",
        heading: "The summary on top",
        terms: [
          {
            term: "Avoidable pain points on quotations a month",
            text: "The cases of each High and Medium pain point above a normal working day or above the best area, added. The caption of the first tile shows it. A quotation with two pain points counts twice.",
          },
          {
            term: "Pain points on quotations a month",
            text: 'The quotations of each High and Medium pain point, added. A quotation with two pain points counts twice. Weak signs, different ways of working, replies and orders are not added. The first tile shows it with the badge "Counted".',
          },
          {
            term: "Counted",
            text: "A figure that adds the cases of the pain points. It is not a count of quotations: a quotation with two pain points counts twice.",
          },
          {
            term: "Hours a month",
            text: 'The large number is an estimate: fixed minutes for each pain point of "Pain points on quotations a month", with all steps, and its cost in EUR. "Of this, search by hand in F&O" is the measured time in the F&O forms, a lower bound.',
          },
          {
            term: "Customer got a reply within 4 working hours",
            text: 'Only a reply that the customer sees counts: a reply mail, the quotation mail, or a reply or question seen in the shared mailbox. An F&O quotation alone is not a reply. The share is of the inquiries with a reply we can see, from the date after the last data change. The caption gives the share of all inquiries that have one. With fewer than 5 cases the tile shows "Not shown" and no count. "First reaction within 4 working hours (reply or F&O quotation)" is the old measure, a second row of the scorecard.',
          },
          {
            term: "Still open 30 days after it was made",
            text: "A quotation with no order, no loss and no cancel 30 days after it was made. All areas use the same 30 days.",
          },
          {
            term: "No decision after 30 days",
            text: "The pain point of the quotations that are still open after 30 days. An area is compared with the best area of the same validity: 30 days with 30 days, 90 days with 90 days. The rule needs 10 points or more, or 50 more quotations a month, than that area.",
          },
          {
            term: "Order intake a month (estimate)",
            text: 'The orders that more decisions or faster replies could bring, at the median value of a won quotation. "Cost of the work a month (estimate)" is another figure: the hours times 60 EUR.',
          },
          {
            term: "Lower bound",
            text: "The real value is this value or more.",
          },
          {
            term: "Inquiries we can follow to a quotation",
            text: 'The share of inquiries with a link to their quotation. "Exact" is a link of F&O or a quotation number in the mail thread. "Strong" shares items with the inquiry. "Inferred" shares only the items or the customer. The figures of the inquiries count the linked ones only. Below 60 %, "Data quality" shows "Low share". This is data quality, not a pain point, so it has no red and no yellow.',
          },
          {
            term: "More figures",
            text: 'On a phone, the summary shows the biggest pain point and two tiles first. Tap "More figures" to see the other sentences, the other two tiles and "Data quality".',
          },
          {
            term: "Page filters",
            text: 'On a phone, the filters of the page are behind "Page filters". The number shows how many filters are set. Tap "Show the figures" to close the filters.',
          },
          {
            term: "Steering group summary",
            text: 'Opens one page to print or to save as PDF. It starts with "For the decision" and the summary. Then it shows the funnel, the speed effect and the 5 biggest area gaps. The heat map, the 5 most severe pain points, the scorecard and the top 10 of "Actions for 9 Nov" follow.',
          },
          {
            term: "The figures are being calculated",
            text: 'The server makes the figures again after a build. The page tries again one time after 5 seconds. If the notice stays, click "Try again" after 1 minute.',
          },
          {
            term: "Link to the page tabs",
            text: 'The address keeps the tab in the key `layer`. For example `?layer=baseline` opens "Baseline for I2Q", and `?layer=why` opens "Why it happens".',
          },
        ],
      },
      {
        // The report download (17 Oct 2026, pm7): labels of
        // features/process-mining/labels.ts (REPORT_LABELS and its neighbours).
        kind: "terms",
        heading: "The report to download",
        terms: [
          {
            term: "Download report",
            text: 'Opens a dialog next to "Steering group summary". Pick the "Audience": each choice shows what its report holds. Pick the "Format": "Word report" or "PowerPoint deck". For "Area leader", also pick the "Area of the report". Then click "Download". The report always uses the figures of the last build, never the filters of the page. The dialog names the build, because the page shows the live window and its figures can differ. If the report is not available, "Download" is off and the dialog says why. Try again in a minute.',
          },
          {
            term: "Audience",
            text: '"Steering group" gets the summary, the decisions asked and the key figures first. Its appendix has the scorecard and a row for each validity class. "Sales leaders" gets where the work hurts in each area and who acts. "Area leader" gets one area against the group and the best area. "Process and IT" gets the pain points with a cause in F&O or in the data.',
          },
          {
            term: "Text written with AI",
            text: 'The texts of the report are written with AI and checked against the figures. The AI never writes a number: the server puts in every figure. The state under the audience tells how the text is made. "Being written. Try again in a minute." means that the AI text comes soon. Until then the report uses the standard text.',
          },
          {
            term: "Standard text",
            text: 'Fixed sentences with the same figures. The report is complete with it. "Standard text: AI writing is off here. The figures are complete." means that this server writes no AI text. The page "Where these figures come from" of the file says which text it holds.',
          },
          {
            term: "PDF of the report",
            text: '"For a PDF, open the file in Word or PowerPoint and save it as PDF." To print the page itself, use "Steering group summary".',
          },
        ],
      },
      {
        kind: "terms",
        heading: "How sure and how far a figure goes",
        terms: [
          {
            term: "Avoidable",
            text: "The cases of a pain point above the comparison: a normal working day, or the best area of the same validity. A pain point with no comparison counts in full.",
          },
          {
            term: "I2Q fit",
            text: '"In I2Q scope": I2Q does this work. "Needs a scope decision": I2Q can help only if the product owner adds it to the scope. "Process action, not I2Q": the area or the process owner fixes it.',
          },
          {
            term: "Interval",
            text: 'The range that holds the true share 19 times in 20, for example "34.9% (31 to 40)". "no clear best" means that the range of the best area touches the value of all areas.',
          },
          {
            term: "Baseline of record",
            text: "The figures of one build, frozen for the decision of 9 Nov. The page shows them beside the figures of today. Until the lead freezes them, the page says that the figures change every night.",
          },
          {
            term: "Proposed target",
            text: 'The lower edge of the 95 % interval of the best area, when that area is clearly best and measured. That level is proven. When lower is better, it is the upper edge. The cell shows the value of the best area next to it. It has the badge "PROPOSED". "Still open 30 days after it was made" and "Ordered within 30 days (paperwork out)" compare only the areas of one validity class. They have one target for each class, on the tab "Areas compared". The steering group accepts or changes it.',
          },
          {
            term: "Data quality",
            text: '"Inquiries linked to a quotation" and "Inquiries linked exactly" show how well the data links an inquiry to its quotation. They are in the group "Data quality" under the scorecard. They are not process targets and get no proposed target.',
          },
          {
            term: "Same quotation made twice",
            text: "The base is every case with a quotation, in all areas. The scorecard says this under the row.",
          },
          {
            term: "Actions for 9 Nov",
            text: 'One row for each High or Medium pain point in an area. A row says who acts, the first action and the measure of success. It also gives the baseline, the proposed target and the day of the check. The status comes from the weekly figures: Better, No change, Worse, Too early, No weekly figures yet, Data changed or Too few weeks. "Data changed" means that a data source started in the window. The page changes nothing.',
          },
          {
            term: "Trend",
            text: '"Rising" or "Falling" needs a clear change over full weeks. "The data changed" means that too few weeks came after the last data change. "No clear change" means that the change can be chance.',
          },
          {
            term: "Quotation mail sent again",
            text: "The share of the quotations with a mail that have 2 or more quotation mails that are not replies. Only mails from 2 hours before to 30 days after the quotation count.",
          },
        ],
      },
      {
        kind: "steps",
        steps: [
          {
            title: "Read the pain points",
            text: 'The first tab "Pain points" shows the worst pain points as tiles: the size, the trend, the worst areas and "Who acts". "Where it hurts" shows each pain point per area. Click a cell or "See what to do" to open the side panel.',
          },
          {
            title: "Act on one pain point",
            text: 'The side panel says "What to do this month", "Who acts" and "How I2Q helps". It shows one chart and the findings behind it. "Show the cases" opens the cases of a finding.',
          },
          {
            title: "Filter and share",
            text: 'Pick "Area", "Window", "Pain point", "Who acts", "How sure" or "Size". The filters live in the address, so a link shows exactly what you see. Send it to the sales manager of an area.',
          },
          {
            title: "Find the Excel file",
            text: 'Open "Tools" and look at "Excel files opened". "Where the file is" shows the path. "Where the file is" in a finding opens that file in the table.',
          },
        ],
      },
      {
        kind: "terms",
        heading: "Pain points",
        terms: [
          {
            term: "Pain points",
            text: "Work that costs time or orders, made of one or more findings, with one role who acts.",
          },
          {
            term: "High",
            text: "At least 2 times the comparison, or 20 points above it, and at least 50 a month. Only for a measured finding.",
          },
          {
            term: "Medium",
            text: "At least 1.3 times the comparison, or 10 points above it, and at least 20 a month.",
          },
          {
            term: "Show weak signs",
            text: 'Shows the findings with severity "Low" too, for example an estimate. "Weak signs to check" gives the reason in words.',
          },
          {
            term: "Cases per month",
            text: 'Ranks "Biggest pain points" by the quotations a month. "Hours per month" ranks them by the measured F&O hours.',
          },
          {
            term: "Table",
            text: 'Shows every finding of the selection in one table. "Download as CSV" saves it for Excel.',
          },
        ],
      },
      {
        kind: "terms",
        heading: "How sure a finding is",
        terms: [
          { term: "Measured", text: "Counted in F&O or in the mails." },
          {
            term: "Strong sign",
            text: "Compared with a normal working day. It shows a pattern, not a cause.",
          },
          {
            term: "Estimate",
            text: 'The hours use an assumed time for each step. The finding names it after "ASSUMED".',
          },
          {
            term: "Normal working day",
            text: "The comparison of a tool figure: the same sales person at the same clock time on the working day before.",
          },
          {
            term: "Checked, not a finding",
            text: "Things that I2Q checked and that are normal daily work, for example CRM.",
          },
        ],
      },
      {
        kind: "terms",
        heading: "The tabs",
        terms: [
          {
            term: "Why it happens",
            text: "Where the inquiries go, where the time goes, how fast the first reply comes, whether speed raises the order rate, and the weeks.",
          },
          {
            term: "Core model",
            text: 'The cause of each pain point in the core model and the 9 Nov actions in three tracks. Then the golden steps with their F&O forms, and "Forms used at quotation create" per area. Each golden step of the compare shows its "Link" to a form, which is inferred. With one area in the page filter, the compare shows that area. Else, pick the area in "Area of this table". On a phone, each golden step is a card.',
          },
          {
            term: "Areas compared",
            text: "Each figure for each area in one area order, with the best area when it is clear. Then the decisions after 30 days per validity, and two levers with a range: a faster reply and the follow-up.",
          },
          {
            term: "Baseline for I2Q",
            text: 'The decision sheet for 9 Nov: the baseline of record, the scorecard with "Better is" and "Proposed target", "Actions for 9 Nov" and "How I2Q is measured after 9 Nov". "I2Q target" stays "Set by the steering group" until the steering group sets it.',
          },
          {
            term: "Details",
            text: 'The four figures behind the pain points: "Value stream", "Process map", "Steps per area" and "Runs".',
          },
          {
            term: "Value stream",
            text: "For each area: the counts from the inquiry to the quotation to the order, the share of orders and the time between the steps.",
          },
          {
            term: "Process map",
            text: 'The steps in the order of the work, with arrows. Each arrow shows the cases and the median time. "Rework loops" lists the arrows that go back.',
          },
          {
            term: "Steps per area",
            text: "Do the areas quote in one global way? The share of each area on the five most frequent ways, and the share of each step.",
          },
          {
            term: "Tools",
            text: 'Which tools run on the devices of sales people between the steps: Outlook, F&O, CRM, Excel, PDF and portals. A tool that runs is not always a tool in use. "Time in tool" is "Not measured". "Excel files opened" names every Excel file that sales people open, the most people first, and shows "Where the file is". Click a column header to sort, and use the filters above the table. The table shows the kinds of file of a quotation step, and "Show every kind of file" shows all. A file in the mail attachment folder reads "Opened from a mail attachment". "Quotation mails" has no column for the shared mailbox: the intake addresses are groups, not a sender, so that share is 0 in every area.',
          },
          {
            term: "Cases",
            text: 'The list of cases. A case shows its steps in time order. "Open in I2Q" opens the case page when your areas include the case.',
          },
          {
            term: "Runs",
            text: 'The builds and the imports of the data. "Old key" marks data that the figures leave out.',
          },
        ],
      },
      {
        // The tab "Core model" (pm8, 17 Oct 2026): labels of features/process-mining/labels.ts
        // (CORE_MODEL_LABELS and its neighbours); the golden map is
        // backend/src/rfq_foundry/process_mining/core_model_map.json.
        kind: "terms",
        heading: "The tab Core model",
        terms: [
          {
            term: "Core model",
            text: "The global way to work in F&O that every area follows. The CMP Book and the ClickLearn recordings of the quotation path describe it.",
          },
          {
            term: "Golden step",
            text: "One step of a ClickLearn recording of the quotation path, for example `SA12-030.s05`. The tab shows the F&O forms of each golden step.",
          },
          {
            term: "Core model design",
            text: "The pain point comes from how the core model is made. A change of the core model fixes it.",
          },
          {
            term: "Core model gap",
            text: "The core model has no step for the work. Each area fills the gap in its own way.",
          },
          {
            term: "Deviation from the core model",
            text: "The area works in a different way than the core model. To follow the core model fixes it.",
          },
          {
            term: "Inferred",
            text: 'A conclusion from the data, not a count. Every cause is inferred until the core model owner confirms it, so the column is "Cause (inferred)". The link from a step to an F&O form is inferred too.',
          },
          {
            term: "Core model owner",
            text: 'The role that confirms each cause and decides on a change of the core model. "Who acts first" names the role that acts in the app. When another role owns the cause, a second line under it names that role.',
          },
          {
            term: "The 9 Nov actions",
            text: 'Each action of "Actions for 9 Nov" is in one track: "Change the core model", "Follow the core model" or "Decide first".',
          },
          {
            term: "Create window",
            text: "From 15 minutes before to 15 minutes after a quotation is made. The shares of the tab are measured in this window.",
          },
          {
            term: "On path",
            text: "A golden step whose forms are in 10 % or more of the create windows of the area, and in at least 5.",
          },
          {
            term: "Rarely seen",
            text: "A golden step whose forms are in less than 10 % of the create windows of the area, or in fewer than 5.",
          },
          {
            term: "Not judged at create",
            text: "A golden step of a later stage, for example the confirmation or the order. The create window does not judge it.",
          },
          {
            term: "Off path",
            text: '"Forms off the golden path" lists the forms that are in 10 % or more of the create windows and are in no golden step. Navigation pages are left out.',
          },
          {
            term: "Off-path form groups",
            text: 'The groups of the off-path forms. The group with the largest "Difference in points" comes first: the "Share at create" minus the "Share in the control window" of one form. So a group that the area uses at every hour does not come first. Each group shows its form with the largest difference and the two shares of that form. The shares are measured.',
          },
          {
            term: "Not shown (fewer than 5 cases)",
            text: "The area has fewer than 5 quotations with page views. The tab shows no share for it.",
          },
          {
            term: "Form names",
            text: "Form names show only on this tab. The downloads show the recording id, its title and the form group.",
          },
          {
            term: "Open points",
            text: "The questions of the golden map that wait for the core model owner or the product owner. The tab lists them at the end.",
          },
        ],
      },
      {
        kind: "terms",
        heading: "The words under each figure",
        terms: [
          { term: "Source", text: "Where the figure comes from: the mails, F&O or Defender." },
          { term: "Window", text: "The days that the figure covers." },
          {
            term: "Coverage",
            text: 'What I2Q could link, with the thing it counts: for example "inquiries linked to a quotation". "Cases" is every case of the figure. A low coverage means that the figure shows only a share of the work.',
          },
          {
            term: "Import window",
            text: "The days of the Defender or F&O import behind a figure. It can be shorter than the window of the page.",
          },
          {
            term: "Not measured",
            text: "I2Q has no data for this figure. The page never shows 0 for a figure without data.",
          },
          {
            term: "Too recent to count",
            text: "The cases are younger than 30 days. An order can still come.",
          },
          {
            term: "ASSUMED",
            text: "The figure rests on an assumption. The line under the figure names it.",
          },
        ],
      },
      {
        kind: "list",
        heading: "Persons stay hidden",
        items: [
          "The page shows areas, roles and steps. It never shows the name of a person.",
          'A person in one case is "Sales person" with a letter. The letter is for that case only.',
          'A figure of fewer than 5 people is shown too. In an area with one sales person, a figure is the work of that person. A figure of fewer than 5 cases shows "Not shown (fewer than 5 cases)".',
          "The page has no list and no ranking of persons.",
          "A pseudonym is not anonymous. A person with access to the database and to F&O can find the person behind it.",
        ],
      },
      {
        kind: "note",
        tone: "info",
        title: "Only for the process mining role",
        text: "For now, only the product owner has the role. The page reads data and changes nothing in F&O, CRM or a mailbox.",
      },
    ],
    labels: [
      "Process mining",
      "Value stream",
      "Process map",
      "Rework loops",
      "Steps per area",
      "Tools",
      "Time in tool",
      "Not measured",
      "Excel files opened",
      "Cases",
      "Open in I2Q",
      "Runs",
      "Old key",
      "Pain points",
      "Pain point",
      "Area",
      "Window",
      "Who acts",
      "How sure",
      "Size",
      "See what to do",
      "Where it hurts",
      "What to do this month",
      "How I2Q helps",
      "Show the cases",
      "Where the file is",
      "High",
      "Medium",
      "Show weak signs",
      "Weak signs to check",
      "Biggest pain points",
      "Avoidable pain points on quotations a month",
      "Pain points on quotations a month",
      "Hours a month",
      "Customer got a reply within 4 working hours",
      "First reaction within 4 working hours (reply or F&O quotation)",
      "Still open 30 days after it was made",
      "No decision after 30 days",
      "Order intake a month (estimate)",
      "Cost of the work a month (estimate)",
      "In I2Q scope",
      "Needs a scope decision",
      "Process action, not I2Q",
      "Actions for 9 Nov",
      "Proposed target",
      "PROPOSED",
      "Data quality",
      "Better is",
      "How I2Q is measured after 9 Nov",
      "Opened from a mail attachment",
      "Quotation mail sent again",
      "Try again",
      "Steps per area",
      "Lower bound",
      "Inquiries we can follow to a quotation",
      "Low share",
      "More figures",
      "Page filters",
      "Show the figures",
      "Area of this table",
      "Exact",
      "Strong",
      "Inferred",
      "Steering group summary",
      "Download report",
      "Audience",
      "Format",
      "Word report",
      "PowerPoint deck",
      "Area leader",
      "Sales leaders",
      "Process and IT",
      "Download",
      "Being written. Try again in a minute.",
      "Area of the report",
      "Counted",
      "Cause (inferred)",
      "Pain points counted on quotations (High and Medium)",
      "Counted a month (High and Medium)",
      "For information",
      "Standard text: AI writing is off here. The figures are complete.",
      "For a PDF, open the file in Word or PowerPoint and save it as PDF.",
      "Why it happens",
      "Areas compared",
      "Baseline for I2Q",
      "I2Q target",
      "Set by the steering group",
      "Cases per month",
      "Hours per month",
      "Table",
      "Download as CSV",
      "Details",
      "Measured",
      "Strong sign",
      "Estimate",
      "Normal working day",
      "Checked, not a finding",
      "Source",
      "Window",
      "Coverage",
      "Import window",
      "Too recent to count",
      "ASSUMED",
      "Sales person",
      "Not shown (fewer than 5 cases)",
      "Core model",
      "Core model design",
      "Core model gap",
      "Deviation from the core model",
      "Core model owner",
      "Who acts first",
      "The 9 Nov actions",
      "Change the core model",
      "Follow the core model",
      "Decide first",
      "Forms used at quotation create",
      "Forms off the golden path",
      "Off-path form groups",
      "Share at create",
      "Share in the control window",
      "Difference in points",
      "On path",
      "Rarely seen",
      "Not judged at create",
      "Not shown (fewer than 5 cases)",
      "Open points",
    ],
  },
  {
    id: "feedback",
    title: "Give feedback",
    summary:
      "Tell us when something broke, a result was wrong, or you have an idea or a question. Use Give feedback at the top of any page and follow the reply under My feedback.",
    keywords: [
      "feedback",
      "bug",
      "error",
      "idea",
      "feature",
      "request",
      "change",
      "question",
      "wrong result",
      "report",
      "triage",
    ],
    blocks: [
      {
        kind: "text",
        text: "The button Give feedback sits at the top of every page. It also sits under More on the case, in the feedback section of the Details sheet, and on the dashboard. Every one of them opens the same short form.",
      },
      {
        kind: "terms",
        heading: "Pick what it is",
        terms: [
          { term: "Something broke", text: "A button, a page or a number does not work." },
          {
            term: "Wrong result from the assistant",
            text: "Wrong customer, item, quantity, price, reply, translation or kind of mail. Say what is right: that makes it a useful example for the build team.",
          },
          { term: "Idea or feature request", text: "Something new that would help." },
          {
            term: "Change how something works",
            text: "Something exists but should work differently.",
          },
          { term: "Question", text: "You are not sure how something works." },
        ],
      },
      {
        kind: "text",
        text: 'In the customer section of the Details sheet and in the Details of each row, "Was this right? Give feedback" asks Right or Wrong. I2Q saves Right at once. Wrong opens the form with the case and the row filled in. To correct the case itself, use Pick item or No item on the row, or Not them and Find customer on the customer line. Feedback tells the build team. It does not change the case.',
      },
      {
        kind: "text",
        text: 'I2Q attaches the page, the case, the line, your area and the app version for you. "What we attach" at the bottom of the form lists them. I2Q attaches the mail text only when you tick "Include the mail text".',
      },
      {
        kind: "text",
        text: 'Your text is safe until you save it. On a case, this browser keeps the text you typed, and the form shows it again the next time you open it on that case. When you close the form with text in it, I2Q asks first: "Keep writing", "Close and keep the text" or "Discard the text". "Start again" clears the text you got back.',
      },
      {
        kind: "text",
        text: "My feedback lists what you sent, its status and the replies. A number next to My feedback means there is news since you last looked. Managers and IT handle the items in Feedback triage.",
      },
      {
        kind: "note",
        tone: "info",
        title: "Nothing leaves the app",
        text: "Feedback stays in Norican I2Q only. I2Q creates no mail, no Teams message and no ticket from it. Do not paste customer mail or prices into the text: I2Q attaches the case for you.",
      },
      {
        kind: "text",
        text: "Discussions lists the open questions and the decisions of record for Norican I2Q. Anyone signed in can open a topic and comment under their name. Managers record the decision.",
      },
      { kind: "link", text: "Open discussions", to: "/discussions" },
    ],
    labels: [
      "Give feedback",
      "More",
      "Details",
      "My feedback",
      "Feedback triage",
      "Was this right? Give feedback",
      "What we attach",
      "Include the mail text",
      "Keep writing",
      "Close and keep the text",
      "Discard the text",
      "Start again",
      "Discussions",
    ],
  },
  {
    id: "contact",
    title: "Who to contact",
    summary:
      "A wrong result or a question: the pilot lead of your area. Access or sign-in: Norican IT. The case number is enough.",
    keywords: ["contact", "support", "help desk", "it", "problem", "wrong", "access"],
    blocks: [
      {
        kind: "terms",
        terms: [
          {
            term: "A result is wrong",
            text: 'Correct it in the case and give feedback "wrong" with one sentence. If it looked finished (a line marked matched, a confirmed customer, a wrong price), tell the pilot lead of your area the same day.',
          },
          {
            term: "A question about I2Q",
            text: "Ask the pilot lead of your area, or the IT manager who runs Norican I2Q.",
          },
          {
            term: "No access, or you see no mailbox",
            text: "Raise a ticket with the Norican IT service desk. Name Norican I2Q and your area.",
          },
        ],
      },
      {
        kind: "note",
        tone: "warning",
        title: "Never forward the mail of the customer",
        text: "To report a problem, send the case number (for example RFQ-d201e62acf0e). It is in the case header.",
      },
    ],
    labels: [],
  },
];

/** The topic of an id. */
export function helpTopic(id: HelpTopicId): HelpTopic {
  const topic = HELP_TOPICS.find((entry) => entry.id === id);
  if (!topic) {
    throw new Error(`Unknown help topic: ${id}`);
  }
  return topic;
}

/** All words of a topic, lower-cased, for the search on the Help page. */
function topicText(topic: HelpTopic): string {
  const parts: string[] = [topic.title, topic.summary, ...topic.keywords];
  for (const block of topic.blocks) {
    switch (block.kind) {
      case "text":
        parts.push(block.text);
        break;
      case "steps":
        block.steps.forEach((step) => parts.push(step.title, step.text));
        break;
      case "terms":
        parts.push(block.heading ?? "");
        block.terms.forEach((term) => parts.push(term.term, term.text, term.system ?? ""));
        break;
      case "list":
        parts.push(block.heading ?? "", ...block.items);
        break;
      case "note":
        parts.push(block.title, block.text);
        break;
      case "link":
        parts.push(block.text);
        break;
      case "legend":
        parts.push(block.heading, ...block.items.map((item) => item.text));
        break;
      case "figure":
        parts.push(block.caption ?? "");
        break;
    }
  }
  return parts.join(" ").toLowerCase();
}

/** The topics that contain every word of the query (all topics for an empty query). */
export function filterHelpTopics(query: string): HelpTopic[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [...HELP_TOPICS];
  }
  return HELP_TOPICS.filter((topic) => {
    const text = topicText(topic);
    return words.every((word) => text.includes(word));
  });
}
